import { useState, useEffect, useRef, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import Accordion from "./shared/Accordion";
import { TableSkeleton } from "./shared/SkeletonComponents";
import { finalizeAQuoteNomination, getAQuoteById, getAllVantRefs, recordSentOptions } from "../Api";
import { applyDraftRows, extractDraftRows } from "./costTableDraft";
import {
  computeOptionGrandTotal,
  resolveScreening,
  isOptionPriceable,
} from "./costCalc";
import {
  mergeAdHocOptions,
  shouldApplyOriginAirportGate,
  isAdHocRateStale,
} from "./adHocOption";
import { copyElementAsPng, downloadElementAsPdf } from "./exportCostTable";
import useCostTableDraft from "./useCostTableDraft";
import { CustomButton2 } from '../components/CustomButton';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faPlane,
  faTruck,
  faCopy,
  faCheck,
  faTimes,
  faPrint,
  faDownload,
  faTable,
  faEdit,
  faSave,
  faFileInvoice,
  faFileExport,
  faCalculator,
  faIdCard,
  faExclamationTriangle,
  faQuestionCircle,
  faCircleInfo,
  faArrowRight,
  faSlidersH,
  faLocationDot,
  faCircleCheck,
  faFileCsv,
  faFilePdf,
  faEnvelope,
  faPaperPlane,
  faEye,
  faEyeSlash,
  faLock,
  faLockOpen,
  faCircleNotch,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { filterUndefinedFields, isObjectEmpty } from "../Helper/FilterationFunctions";
import { showSwal } from "../Helper/ShowAlert";
import { useDarkMode } from "../context/DarkModeContext";
import { motion, AnimatePresence } from "framer-motion";
import Tooltip from "./shared/Tooltip";
import toast from "react-hot-toast";
import { buildCostTableCsv } from "./finalQuoteCsv";
// import { saveAs } from "file-saver";
// import { CSVLink } from "react-csv";


function MaybeAccordion({ noAccordion, children, ...accordionProps }) {
  if (noAccordion) return <>{children}</>;
  return <Accordion {...accordionProps}>{children}</Accordion>;
}

/* Chrome and Safari step a FOCUSED <input type="number"> on mouse wheel and
   fire change, indistinguishable from typing. Every number field in this table
   writes straight into the quote's pricing — the "Adjust common" three write
   into EVERY airline x transport row — and the draft autosave then persists it,
   so one stray scroll silently reprices a quote the operator is only reading.
   Dropping focus before the browser applies its default action disarms the
   wheel; deliberate arrow-key stepping is untouched. */
const blurOnWheel = (e) => e.currentTarget.blur();

/**
 * Transit time, with a unit and without lying.
 *
 * The column rendered the stored value verbatim under a bare "Transit" header:
 * a customer saw "4" and could not tell 4 hours from 4 days. Worse, records
 * whose transitTime is unset rendered the literal string "undefined" into a
 * customer-facing table. A missing value is now an em dash.
 */
const formatTransit = (v) => {
  if (v === null || v === undefined) return "—";
  const s = String(v).trim();
  if (!s || s.toLowerCase() === "undefined" || s.toLowerCase() === "null") return "—";
  // Already carries a unit ("2 days", "48h") — leave the operator's words alone.
  if (/[a-z]/i.test(s)) return s;
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return `${n} ${n === 1 ? "day" : "days"}`;
};

const money = (n) =>
  Number(n || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * The derivation behind a row's total, on demand.
 *
 * "I could not reproduce £3.96/kg from the visible inputs. There's no
 * breakdown, no tooltip, no min-charge indication. For a pricing tool this is
 * the single biggest trust gap." — so every line the calculator used is listed,
 * and any figure that was floored at a minimum charge says so, because a row
 * priced entirely off a minimum looks identical to one priced off a rate.
 */
function CostBreakdown({ lineItems, total, anyMinApplied }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Show how this total was calculated"
        className="font-semibold text-green-700 underline decoration-dotted underline-offset-2 hover:text-green-800 dark:text-emerald-400"
      >
        £&nbsp;{total}
      </button>
      {anyMinApplied && (
        <span
          title="A minimum charge was applied on at least one line — the per-kg total was lower."
          className="rounded bg-amber-100 px-1 text-[9px] font-bold uppercase text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
        >
          min
        </span>
      )}
      {open && (
        <div
          role="dialog"
          aria-label="Cost breakdown"
          className="absolute right-0 top-full z-40 mt-1 w-[280px] rounded-xl border border-slate-200 bg-white p-3 text-left shadow-xl dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)]"
        >
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400">
            How this total is built
          </div>
          <table className="w-full text-[11.5px]">
            <tbody>
              {lineItems.map((l, i) => (
                <tr key={`${l.label}-${i}`} className="align-top">
                  <td className="py-[2px] pr-2 text-slate-600 dark:text-slate-300">
                    {l.label}
                    {l.basis === "per-kg" && l.rate ? (
                      <span className="block text-[10px] text-slate-400">
                        {l.rate} × {l.qty} kg
                        {l.minApplied ? ` · min £${money(l.minCharge)} applied` : ""}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-[2px] text-right font-medium tabular-nums text-slate-800 dark:text-slate-100">
                    £ {money(l.amount)}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-slate-200 dark:border-[oklch(0.33_0.022_264)]">
                <td className="pt-1.5 font-semibold text-slate-900 dark:text-slate-100">
                  Total
                </td>
                <td className="pt-1.5 text-right font-bold tabular-nums text-green-700 dark:text-emerald-400">
                  £ {total}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </span>
  );
}

const clockTime = (d) =>
  d instanceof Date && !Number.isNaN(d.getTime())
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

/**
 * Whether the operator's pricing is in the database, stated plainly.
 *
 * The table used to say nothing at all, so the only way to find out was to
 * close it and reopen — the very action that was losing the work.
 */
function DraftStatusChip({ status, lastSavedAt, error }) {
  const at = clockTime(lastSavedAt);
  const look = {
    saving: {
      icon: faCircleNotch,
      spin: true,
      text: "Saving…",
      cls: "border-slate-200 bg-white text-slate-500 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-400",
    },
    dirty: {
      icon: faCircleInfo,
      text: "Unsaved changes",
      cls: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
    },
    error: {
      icon: faTriangleExclamation,
      text: `Not saved — ${error || "unknown error"}`,
      cls: "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
    },
    saved: {
      icon: faCircleCheck,
      text: "Saved just now",
      cls: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
    },
    clean: {
      icon: faCircleCheck,
      text: at ? `Saved ${at}` : "Nothing to save yet",
      cls: "border-slate-200 bg-white text-slate-500 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-400",
    },
  }[status] || {};

  if (!look.text) return null;
  return (
    <span
      role="status"
      aria-live="polite"
      title={status === "error" ? error : undefined}
      className={`inline-flex max-w-[260px] items-center gap-1.5 truncate rounded-full border px-2.5 py-1 text-[11.5px] font-semibold ${look.cls}`}
    >
      <FontAwesomeIcon
        icon={look.icon}
        className={`text-[10px] ${look.spin ? "animate-spin" : ""}`}
        aria-hidden
      />
      <span className="truncate">{look.text}</span>
    </span>
  );
}

/**
 * Lightweight help popover: a question-mark button that toggles a rich card.
 * Use inline next to any section heading or in an accordion headerAction to
 * explain what the section does without bloating the visible UI.
 */
function HelpPopover({ label = "Walkthrough", title, sections = [], align = "right", size = "md" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const panelWidthClass = size === "lg" ? "w-[min(92vw,420px)]" : "w-[min(92vw,340px)]";
  const alignClass = align === "right" ? "right-0" : "left-0";

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${open
          ? "border-blue-200 bg-blue-50 text-blue-600 dark:border-blue-500/30 dark:bg-blue-500/15 dark:text-blue-300"
          : "border-slate-200 bg-white text-slate-500 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-400 dark:hover:bg-blue-500/10 dark:hover:text-blue-300"
          }`}
        aria-expanded={open}
        aria-label={label}
      >
        <FontAwesomeIcon icon={faQuestionCircle} className="text-[11px]" />
        <span>{label}</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={`absolute top-full mt-2 ${alignClass} z-50 ${panelWidthClass} rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_20px_48px_-20px_rgba(15,23,42,0.25)] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)]`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
                  <FontAwesomeIcon icon={faCircleInfo} className="text-[12px]" />
                </span>
                <h5 className="truncate text-[13px] font-semibold text-slate-900 dark:text-slate-100">
                  {title || label}
                </h5>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-[oklch(0.26_0.028_264)]"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} className="text-[11px]" />
              </button>
            </div>
            <ol className="mt-3 space-y-3 text-[12px] leading-relaxed text-slate-600 dark:text-slate-300">
              {sections.map((s, i) => (
                <li key={i} className="flex gap-2.5">
                  <span className="mt-[1px] inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600 dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-300">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    {s.heading && (
                      <div className="font-semibold text-slate-900 dark:text-slate-100">
                        {s.heading}
                      </div>
                    )}
                    <div className="text-slate-500 dark:text-slate-400">
                      {s.body}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const TABLE_WALKTHROUGH = [
  {
    heading: "Available combinations",
    body: "Each saved airline is paired with transport agents whose destination airports match the airline's origin. That's how Total Combinations is derived.",
  },
  {
    heading: "Pick a screening mode",
    body: "Inside each row choose PTS / STS (transport screening, flat £) or PAS / SAS (airline screening, per-kg). Only one can be selected per row.",
  },
  {
    heading: "Tune profit & extras",
    body: "Profit Margin (%), Customs Flat (£) and Extra Charges (£) are editable per row. Use 'Adjust Common' to apply the same values across every row at once.",
  },
  {
    heading: "Final & Total rate",
    body: "Final Rate (£/kg) = base cost × (1 + margin) + extras, per kg. Total Rate (£) is the chargeable-weight total you will quote to the customer.",
  },
  {
    heading: "Lock, then finalise",
    body: "Click 'Lock Selections' to freeze your row picks, then 'Finalise Quote' to lock a single winning option. A valid VANT Ref Id is required.",
  },
];

const VANT_WALKTHROUGH = [
  {
    heading: "What is VANT Ref Id?",
    body: "A permanent reference stamped on every finalised quote: company prefix + user initials + year + month + sequence.",
  },
  {
    heading: "Auto-filled vs editable",
    body: "Company, user, year and month are filled from your profile and today's date. Only the sequence number is editable.",
  },
  {
    heading: "Sequence rule",
    body: "The sequence must be strictly greater than the last assigned value. The badge turns green only when the whole ID is valid.",
  },
];

const ADJUST_COMMON_WALKTHROUGH = [
  {
    heading: "Bulk-apply shortcut",
    body: "Changing a value here overwrites Profit Margin / Custom Rate / Extra Charges on every airline × transport row below.",
  },
  {
    heading: "When to use",
    body: "Best for a baseline before fine-tuning individual rows. After locking selections, per-row values are frozen for review.",
  },
];

// Manual-query callers (ManualQueryDetail) don't drive the side-rail panel
// scroll/focus the way QuoteAllDetails does, so they don't pass tableRefs /
// setItemRefs / triggerPanel. Provide safe defaults — without these, the
// `setItemRefs(...)` call inside fetchQuoteDetails threw, the catch flipped
// the component into the error state, and no cost table ever rendered.
const NOOP_TABLE_REFS = { current: {} };
const noop = () => { };

/* Rate validity, which the row never showed. A rate that lapsed last month
   prices exactly like a live one, and the operator has no way to tell — so an
   expired rate is named as expired rather than quietly quoted. Returns null when
   nothing is on record: an absent date is not the same as a valid one, and
   inventing "valid" would be the worse lie. */
function rateValidity(raw) {
  if (!raw) return null;
  const until = new Date(raw);
  if (Number.isNaN(until.getTime())) return null;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return {
    expired: until < startOfToday,
    label: until.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
    }),
  };
}

/* The table is denominated in £ and does not convert. A rate captured in another
   currency is therefore added to a sterling total as if it were sterling, which
   is wrong by whatever the FX rate is — so say so on the row rather than render
   a confident £ figure built from AED. We do not invent a conversion: there is
   no rate source in this product, and a made-up one is worse than a flag. */
const BASE_CURRENCY = "GBP";
function foreignCurrency(code) {
  const c = (code || "").trim().toUpperCase();
  return c && c !== BASE_CURRENCY ? c : null;
}

const SERVICE_SCOPE_LABELS = {
  "door-to-door": "Door to door",
  "door-to-airport": "Door to airport",
  "airport-to-door": "Airport to door",
  "airport-to-airport": "Airport to airport",
};

/* What is NOT in the figure. Derived from the scope rather than boilerplate,
   because "collection from your premises" is an exclusion on an
   airport-to-airport quote and included on a door-to-door one — and a customer
   who assumes the wrong one has been misquoted either way. Duty, VAT and
   insurance are excluded on every scope: nothing in this table prices them. */
function customerExclusions(scope) {
  const always = [
    "import duty, VAT and any customs examination charges",
    "cargo insurance",
  ];
  if (scope === "airport-to-airport") {
    return ["collection from origin", "delivery at destination", ...always];
  }
  if (scope === "door-to-airport") return ["delivery at destination", ...always];
  if (scope === "airport-to-door") return ["collection from origin", ...always];
  return always;
}

/* The quotation cannot outlive the supplier rate it was built from, so the
   EARLIEST validity across the quoted rows is the honest limit. Returns null
   when nothing is on record — promising a date we cannot stand behind is worse
   than stating no date at all. */
function earliestValidity(options) {
  const dates = (options || [])
    .map(o => o?.airline?.rateValidityUntil)
    .filter(Boolean)
    .map(d => new Date(d))
    .filter(d => !Number.isNaN(d.getTime()));
  if (!dates.length) return null;
  return new Date(Math.min(...dates.map(d => d.getTime())));
}
// Per-browser, not per-quote: an operator who wants the inline table wants it
// on the next quote too.
const FULLSCREEN_OPT_OUT_KEY = "fk.costTable.noAutoFullscreen";

/**
 * One priced block of the cost table: a transport agent and every airline it can
 * be paired with, one row per screening destination.
 *
 * Declared at MODULE scope on purpose. It used to live inside FinalQuoteCostTable,
 * which gave it a new function identity on every render — so React treated it as a
 * different component type and remounted this whole subtree instead of updating it.
 * Typing into a per-row Margin/Customs/Extra field destroyed the input on the first
 * keystroke, which is why every figure that ever reached the database was a single
 * digit.
 */
function QuoteCostTableBlock({
  quoteOptions,
  isCustomerMode,
  isAllOptions,
  indeX,
  saveThisTable,
  handleChange,
  onScreeningSelectChange,
  title,
  refs,
  agentC,
  isFinalizedQuote = false,
  // Were closure reads before the hoist; passed explicitly now.
  isDarkMode,
  selectedScreening,
  tableRefs,
}) {
  const Destination = quoteOptions[0].destination;

  /* A row with no entry in `selectedScreening` is a row nobody has chosen a
     screening for, and it is left that way.

     It used to be filled in DURING RENDER — onScreeningSelectChange, which is
     setState on the parent, called from inside this component's render pass —
     and what it filled in was wrong: `displayPrimary`, the TRANSPORT PRIMARY
     figure, filed under whatever type the finalised option carried. For a row
     finalised on PAS or SAS that figure should be the airline per-kg rate (see
     screeningValueFor in costTableDraft.js), so the row priced against money
     nobody quoted — and the draft then persisted a choice the operator never
     made.

     Nothing replaces it. The one legitimate default, a finalised option's own
     screening, is already merged in from finalizedQuoteOption.selectedScreening
     when the quote loads, with its stored value and type together. Checked
     against the live database: all 11 finalised quotes key that map exactly as
     the rebuilt rows are keyed, so the merge covers every row and this seed
     never fired on real data — it only ever fabricated. */
  // Pre-compute whether any combination row will survive the origin/destination
  // filter below. Manual-query flow saves transporter rates with default
  // destinationAirport "any" and airlines often have no originAirports set,
  // so every row gets filtered out and the user sees an empty table chrome
  // with no clue what's missing.
  const combinationDiagnostics = (() => {
    if (isFinalizedQuote) {
      return { hasRows: true, missingOrigins: [], anyDestOnly: false };
    }
    let hasRows = false;
    const missingOrigins = [];
    let anyDestOnly = false;
    quoteOptions.forEach(option => {
      const airlineCode =
        option?.airline?.airlineCode || option?.airline?.name || "Airline";
      const origins = Array.isArray(option?.airline?.originAirports)
        ? option.airline?.originAirports
          .filter(a => typeof a === "string" && a.trim() !== "")
          .map(a => a.toLowerCase().trim())
        : [];
      if (origins.length === 0) {
        if (!missingOrigins.includes(airlineCode)) {
          missingOrigins.push(airlineCode);
        }
        return;
      }
      const valid = (option.screeningPrices || []).filter(sp => {
        const dest = (sp?.destinationAirport || "").toLowerCase().trim();
        if (!dest || dest === "any") return false;
        return origins.includes(dest);
      });
      if (valid.length > 0) {
        hasRows = true;
      } else if (
        (option.screeningPrices || []).every(sp => {
          const dest = (sp?.destinationAirport || "").toLowerCase().trim();
          return !dest || dest === "any";
        })
      ) {
        anyDestOnly = true;
      }
    });
    return { hasRows, missingOrigins, anyDestOnly };
  })();
  return (
    <motion.div
      ref={el => {
        if (tableRefs?.current) tableRefs.current[refs] = el;
      }}
      className="overflow-hidden mb-5 rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_8px_24px_-16px_rgba(15,23,42,0.08)] dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)]"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {title && (
        <div className="border-b border-slate-100 bg-[linear-gradient(180deg,#f8fafc_0%,#ffffff_100%)] px-4 py-2.5 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)]">
          {title}
        </div>
      )}
      {/* The four screening codes decide what a row costs, and until now they
          were expanded only in a hover tooltip on each tile — "What does PTS
          mean?" was one of the questions an operator listed as confusing. A
          legend that is always on screen costs one line. Internal only: a
          customer has no use for our screening vocabulary. */}
      {!isCustomerMode && !isFinalizedQuote && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-100 px-4 py-1.5 text-[10.5px] text-slate-500 dark:border-[oklch(0.3_0.02_264)] dark:text-slate-400">
          <span className="font-semibold uppercase tracking-[0.1em] text-slate-400">
            Screening
          </span>
          <span><b className="text-slate-600 dark:text-slate-300">PTS/STS</b> = Primary/Secondary Transport Screening</span>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <span><b className="text-slate-600 dark:text-slate-300">PAS/SAS</b> = Primary/Secondary Airline Screening</span>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <span>charged per kg, floored at the rate's minimum charge</span>
        </div>
      )}
      {/* Every figure below is added up as sterling. When a rate was captured in
          another currency we have no rate source to convert it with, so the
          total is wrong by whatever the FX rate is — and the operator has to be
          told that outright, not left to notice a three-letter badge. */}
      {!isCustomerMode && (() => {
        const codes = [
          ...new Set(
            quoteOptions
              .map(o => foreignCurrency(o?.airline?.currency))
              .filter(Boolean)
          ),
        ];
        if (!codes.length) return null;
        return (
          <div
            role="note"
            className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-amber-200 bg-amber-50/70 px-4 py-1.5 text-[10.5px] text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
          >
            <span className="font-semibold uppercase tracking-[0.1em]">
              {codes.join(" · ")}
            </span>
            <span>
              rate{codes.length > 1 ? "s are" : " is"} not converted — totals
              below add it up as if it were £
            </span>
          </div>
        );
      })()}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur text-[10.5px] uppercase tracking-[0.08em] dark:bg-[oklch(0.225_0.026_264/0.9)]">
            <tr className="border-b border-slate-200 dark:border-[oklch(0.33_0.022_264)]">
              <th className="sticky left-0 z-10 border-r border-slate-200 bg-slate-50/95 p-2 text-center font-semibold text-slate-500 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.9)] dark:text-slate-400">
                <div className="flex items-center justify-center gap-2">
                  <span>ID</span>
                  {!isCustomerMode && !isFinalizedQuote && (
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 cursor-pointer rounded border-slate-300 accent-fk-accent"
                      onChange={(e) => {
                        handleChange(null, "selectBox", e.target.checked, true, indeX, null);
                      }}
                      title="Check/Uncheck All"
                    />
                  )}
                </div>
              </th>
              <th className="p-2 text-left font-semibold text-slate-500 dark:text-slate-400">
                Airline
              </th>
              <th className="p-2 text-right font-semibold text-slate-500 dark:text-slate-400">
                CW <span className="font-normal normal-case text-slate-400">(kg)</span>
              </th>
              {!isCustomerMode ? (
                <>
                  <th className="p-2 text-right font-semibold text-slate-500 dark:text-slate-400">
                    Freight <span className="font-normal normal-case text-slate-400">£/kg</span>
                  </th>
                  <th className="p-2 text-right font-semibold text-slate-500 dark:text-slate-400" title="War Risk Surcharge">
                    War Risk<span className="font-normal normal-case text-slate-400"> £/kg</span>
                  </th>
                  <th className="p-2 text-right font-semibold text-slate-500 dark:text-slate-400">
                    Handling <span className="font-normal normal-case text-slate-400">£/kg</span>
                  </th>
                  <th className="p-2 text-right font-semibold text-slate-500 dark:text-slate-400">
                    Fuel <span className="font-normal normal-case text-slate-400">%</span>
                  </th>
                  <th className="p-2 text-center font-semibold text-slate-500 dark:text-slate-400" title="Pick one screening method per row">
                    <div>Screening</div>
                    <div className="mt-0.5 flex justify-around gap-2 text-[9.5px] font-normal normal-case text-slate-400">
                      <span>Transport&nbsp;£</span>
                      <span>Airline&nbsp;£/kg</span>
                    </div>
                  </th>
                  <th className="p-2 text-right font-semibold text-slate-500 dark:text-slate-400">
                    Transport <span className="font-normal normal-case text-slate-400">£</span>
                  </th>
                  <th className="p-2 text-center font-semibold text-slate-500 dark:text-slate-400">
                    Margin <span className="font-normal normal-case text-slate-400">%</span>
                  </th>
                  <th className="p-2 text-center font-semibold text-slate-500 dark:text-slate-400">
                    Customs <span className="font-normal normal-case text-slate-400">£</span>
                  </th>
                  <th className="p-2 text-center font-semibold text-slate-500 dark:text-slate-400">
                    Extra <span className="font-normal normal-case text-slate-400">£</span>
                  </th>
                </>
              ) : (
                <>
                  <th className="p-2 text-center font-semibold text-slate-500 dark:text-slate-400">
                    Origin
                  </th>
                  <th className="p-2 text-center font-semibold text-slate-500 dark:text-slate-400">
                    Destination
                  </th>
                  <th className="p-2 text-center font-semibold text-slate-500 dark:text-slate-400">
                    Transit
                  </th>
                </>
              )}
              <th className="border-l border-slate-200 bg-emerald-50/50 p-2 text-right font-semibold text-emerald-700 dark:border-[oklch(0.33_0.022_264)] dark:bg-emerald-500/5 dark:text-emerald-300">
                Final Rate <span className="font-normal normal-case text-emerald-500/70">£/kg</span>
              </th>
              <th className="bg-emerald-50/50 p-2 text-right font-semibold text-emerald-700 dark:bg-emerald-500/5 dark:text-emerald-300">
                Total <span className="font-normal normal-case text-emerald-500/70">£</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {!combinationDiagnostics.hasRows && (
              <tr>
                <td colSpan={20} className="px-4 py-6">
                  <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-[12.5px] text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                    <p className="font-semibold">
                      No combinations to compute yet
                    </p>
                    <p className="mt-1 leading-relaxed">
                      Rows here only appear when an airline's <strong>origin
                        airports</strong> match a transporter's screening{" "}
                      <strong>destination airport</strong>. Right now:
                    </p>
                    <ul className="mt-1.5 list-disc space-y-0.5 pl-5">
                      {combinationDiagnostics.missingOrigins.length > 0 && (
                        <li>
                          Airline
                          {combinationDiagnostics.missingOrigins.length === 1
                            ? ""
                            : "s"}{" "}
                          {combinationDiagnostics.missingOrigins
                            .map(c => `"${c}"`)
                            .join(", ")}{" "}
                          {combinationDiagnostics.missingOrigins.length === 1
                            ? "has"
                            : "have"}{" "}
                          no origin airports configured. Open the airline lane
                          and add at least one.
                        </li>
                      )}
                      {combinationDiagnostics.anyDestOnly && (
                        <li>
                          Transporter screening rows still use destination{" "}
                          <code className="rounded bg-amber-100 px-1 dark:bg-amber-900/40">
                            any
                          </code>
                          . Edit the transporter charges and pick a real
                          destination airport (e.g. LHR).
                        </li>
                      )}
                      {combinationDiagnostics.missingOrigins.length === 0 &&
                        !combinationDiagnostics.anyDestOnly && (
                          <li>
                            None of the transporter destination airports match
                            the airline origin airports.
                          </li>
                        )}
                    </ul>
                  </div>
                </td>
              </tr>
            )}
            {quoteOptions.flatMap((option, optionIndex) => {
              // skip the filter on validScreeningPrices if its finalized quote
              let validScreeningPrices
              // An ad-hoc option has no directory records to reconcile — the
              // operator typed both sides — so the origin-airport gate can only
              // make their own rate disappear from the table.
              if (shouldApplyOriginAirportGate(option, isFinalizedQuote)) {
                // Apply the same filtering logic as totalCombinations
                const airlineOriginAirports = Array.isArray(option.airline?.originAirports) ? option.airline?.originAirports : [];
                const normalizedOrigins = airlineOriginAirports
                  .filter(a => typeof a === 'string' && a.trim() !== '')
                  .map(a => a.toLowerCase().trim());

                // If no valid origins, skip this option entirely (matches totalCombinations guard)
                if (normalizedOrigins.length === 0) {
                  return [];
                }

                // Filter screening prices to only include those that match airline origin airports
                validScreeningPrices = option.screeningPrices.filter(sp => {
                  const dest = (sp?.destinationAirport || '').toLowerCase().trim();
                  if (!dest || dest === 'any') return false;
                  return normalizedOrigins.includes(dest);
                });

                // If no valid screening prices, skip this option
                if (validScreeningPrices.length === 0) {
                  return [];
                }
              }

              const CW = option.cumulativeVolumeWeight;
              const FR = option.airline?.ratePerVolumeWeight || 0;
              const WR =
                option.airline?.FFWH?.warRiskSurcharge?.ratePerVolumeWeight ??
                option.airline?.FFWH?.warRiskSurcharge ??
                0;
              const HF =
                option.airline?.FFWH?.handlingFee?.ratePerVolumeWeight ??
                option.airline?.FFWH?.handlingFee ??
                0;
              const FS =
                option.airline?.FFWH?.fuelSurcharge?.ratePerVolumeWeight ??
                option.airline?.FFWH?.fuelSurcharge ??
                0;
              const TC = option.transportCost || 0;
              const pasVal =
                option.airline?.screeningPrices?.primary?.ratePerVolumeWeight ??
                option.airline?.screeningPrices?.primary ??
                0;
              const sasVal =
                option.airline?.screeningPrices?.secondary?.ratePerVolumeWeight ??
                option.airline?.screeningPrices?.secondary ??
                0;
              const computeForScreening = (sp, selected) => {
                const CF = sp.custom ?? option.custom ?? 0;
                const PM = sp.profitMargin ?? option.profitMargin ?? 0;
                const ExC = sp.extra ?? option.extra ?? 0;
                /* Transport screening (PTS/STS). This used to read
                       Math.max(ratePerKg, minCharge)
                   — comparing £0.18 PER KG against a £22 minimum CHARGE and
                   charging £22, because the weight was never applied. The chip
                   the operator clicks showed £103.68 (0.18 x 576) for the same
                   screening, so the button and the row priced the same choice
                   differently. That is one of the three totals the operator
                   found for a single shipment. resolveScreening applies one rule
                   for transport and airline screening alike. */
                const tPrimary = resolveScreening({
                  flat: sp.primary,
                  ratePerKg: sp.primaryScreeningPrice?.ratePerVolumeWeight,
                  minCharge: sp.primaryScreeningPrice?.minCharge,
                  CW,
                });
                const tPrimaryTotal = tPrimary.amount;

                const tSecondary = resolveScreening({
                  flat: sp.secondary,
                  ratePerKg: sp.secondaryScreeningPrice?.ratePerVolumeWeight,
                  minCharge: sp.secondaryScreeningPrice?.minCharge,
                  CW,
                });
                const tSecondaryTotal = tSecondary.amount;

                // Airline screening (PAS/SAS): per-kg, same rule.
                const aPrimary = resolveScreening({
                  ratePerKg:
                    option.airline?.screeningPrices?.primary?.ratePerVolumeWeight ??
                    option.airline?.screeningPrices?.primary,
                  minCharge: option.airline?.screeningPrices?.primary?.minCharge,
                  CW,
                });
                const aPrimaryTotal = aPrimary.amount;

                const aSecondary = resolveScreening({
                  ratePerKg:
                    option.airline?.screeningPrices?.secondary?.ratePerVolumeWeight ??
                    option.airline?.screeningPrices?.secondary,
                  minCharge: option.airline?.screeningPrices?.secondary?.minCharge,
                  CW,
                });
                const aSecondaryTotal = aSecondary.amount;

                const isTSc = selected?.type === "PTS" || selected?.type === "STS";
                const isFSc = selected?.type === "PAS" || selected?.type === "SAS";

                /* Not "is there a freight rate?" but "is anything priced?".
                   A quote-sheet option carries no headline per-kg rate at all —
                   it prices in named charge lines — and this guard rendered it
                   as N/A / £0.00 over real money. */
                if (!isOptionPriceable(option)) {
                  return {
                    destinationAirport: sp.destinationAirport,
                    FinalRate: "N/A",
                    GrandTotal: "0.00",
                    dataForCalculation: {},
                    sp,
                  };
                }

                /* ONE calculator. This file used to carry three divergent copies
                   of this formula — the render path, the effect that builds
                   dataForCalculation, and the finalise path — which is how one
                   shipment ended up with three different totals. costCalc.js was
                   already written and tested as the authoritative version and
                   nothing imported it. It does now, and it hands back its own
                   working so the Total cell can show how it got there. */
                const screeningAmount = isFSc
                  ? (selected?.type === "PAS" ? aPrimaryTotal : aSecondaryTotal)
                  : isTSc
                    ? (selected?.type === "PTS" ? tPrimaryTotal : tSecondaryTotal)
                    : 0;

                const calc = computeOptionGrandTotal({
                  CW,
                  FR,
                  WR,
                  HF,
                  FS,
                  freightMin: option.airline?.FFWH?.freightCharge?.minCharge,
                  warRiskMin: option.airline?.FFWH?.warRiskSurcharge?.minCharge,
                  handlingMin: option.airline?.FFWH?.handlingFee?.minCharge,
                  customs: CF,
                  transportCost: TC,
                  screeningFixed: screeningAmount,
                  profitMarginPct: PM,
                  extra: ExC,
                });

                const finalRateNum = calc.grandTotal;
                const GrandTotal = Number(finalRateNum).toFixed(2);
                const finalRate =
                  calc.finalRate === null ? "N/A" : calc.finalRate.toFixed(2);
                const screeningLine = isFSc
                  ? (selected?.type === "PAS" ? aPrimary : aSecondary)
                  : isTSc
                    ? (selected?.type === "PTS" ? tPrimary : tSecondary)
                    : null;

                return {
                  destinationAirport: sp.destinationAirport,
                  FinalRate: finalRate,
                  GrandTotal,
                  /* The working behind the number, so the operator can audit a
                     price instead of trusting it. Includes the screening line,
                     which the calculator only receives as a resolved amount. */
                  lineItems: screeningLine
                    ? calc.lineItems.map(l =>
                        l.label === "Screening"
                          ? {
                              ...l,
                              basis: screeningLine.basis,
                              rate: screeningLine.rate,
                              minApplied: screeningLine.minApplied,
                              minCharge: screeningLine.minCharge,
                              label: `Screening (${selected?.type || ""})`,
                            }
                          : l
                      )
                    : calc.lineItems,
                  anyMinApplied:
                    calc.anyMinApplied || Boolean(screeningLine?.minApplied),
                  dataForCalculation: {
                    ScreeningID: `${option.airline?.airlineCode}_${option.agentCode}_${sp.destinationAirport}`,
                    SelectedScreening: selectedScreening[`${option.airline?.airlineCode}_${option.agentCode}_${sp.destinationAirport}`],
                    CW,
                    FR,
                    WR,
                    HF,
                    FS,
                    TC,
                    PM,
                    CF,
                    ExC,
                    finalRate,
                    GrandTotal,
                    /* The figure the margin percentage was applied to — i.e.
                       everything we actually pay out. Carried so the finalise
                       path can derive profit from the same calculation that
                       produced the price, instead of subtracting a
                       differently-shaped cost from it. */
                    baseCost: calc.baseCost,
                    screeningAmount,
                    destinationAirport: sp.destinationAirport,
                    selectedAirlineId: option.airline?.airlineId,
                    selectedTransporterId: option.selectedTransporterId,
                    from: option.origin,
                  },
                  sp,
                };
              };

              /* validScreeningPrices is only populated when the origin-airport
                 gate ran. It is skipped for a finalized quote and for an ad-hoc
                 option, and both must then fall back to the option's own
                 screening rows — defaulting to [] instead would render the row
                 with no screening at all, i.e. silently drop it. */
              const screeningArr = validScreeningPrices || option.screeningPrices || [];
              const results = screeningArr.map((sp) => {
                const selKey = `${option.airline?.airlineCode}_${option.agentCode}_${sp.destinationAirport}`;
                const selected = selectedScreening[selKey];
                return {
                  selKey,
                  computed: computeForScreening(sp, selected),
                };
              });
              return results.map(({ selKey, computed }, idx) => {
                const { FinalRate, GrandTotal, sp, dataForCalculation } = computed;
                /* Rows are rendered from a FILTERED view of option.screeningPrices
                   (only the destinations this airline flies from), but handleChange
                   writes by POSITION into the unfiltered array. Whenever anything is
                   filtered out those indices diverge, and the operator's margin lands
                   on a destination they never saw. Resolve the row by identity. */
                /* Resolve this rendered row against the UNFILTERED array that
                   handleChange writes to. -1 means the row could not be
                   resolved, and handleChange then writes nothing at all —
                   never to a fallback position, which is what put a margin on
                   a destination the operator never saw. */
                const spIdx = option.screeningPrices.indexOf(sp);
                const displayPrimary =
                  sp.primary ??
                  (sp.primaryScreeningPrice?.ratePerVolumeWeight
                    ? sp.primaryScreeningPrice.ratePerVolumeWeight * CW
                    : sp.primaryScreeningPrice?.minCharge ?? 0);
                const displaySecondary =
                  sp.secondary ??
                  (sp.secondaryScreeningPrice?.ratePerVolumeWeight
                    ? sp.secondaryScreeningPrice.ratePerVolumeWeight * CW
                    : sp.secondaryScreeningPrice?.minCharge ?? 0);
                const selectedForRow = selectedScreening[selKey];
                // Customer preview hides rows the operator hasn't checked when
                // there are many options to curate — but the finalised row IS
                // the single chosen option, so always render it regardless of
                // the legacy selectBox flag. Without this bypass, the
                // customer-mode shadow used for "Send to Customer Conversation"
                // produced an empty tbody → email got an empty customer table
                // OR fell back to the internal-mode DOM elsewhere.
                if (isCustomerMode && !sp.selectBox && !isFinalizedQuote) {
                  return <></>;
                }
                const selectedST = selectedForRow ? selectedForRow.type : '';
                return (
                  <tr key={`${agentC}-${sp.destinationAirport}-${option.airline?.airlineCode}-${Destination}`} className="border-t text-sm">
                    {!isCustomerMode && !isFinalizedQuote ? (
                      <td className="p-0 text-left text-gray-800 font-medium sticky left-0 bg-white z-10 border-r border-slate-100">
                        <label className={`flex items-center gap-2 px-3 py-[7px] w-full h-full min-h-[44px] transition-colors select-none ${Number(GrandTotal) > 0 ? "cursor-pointer hover:bg-blue-50/50" : "cursor-not-allowed opacity-60"}`}>
                          <span className="text-gray-500 w-[60px] text-right word-break leading-tight text-xs">
                            {agentC}-{sp.destinationAirport}-{option.airline?.airlineCode}
                            <span className="text-[10px] text-slate-400">-{Destination}</span>
                          </span>
                          <div className="flex items-center justify-center p-1 border rounded bg-white shadow-sm hover:border-blue-400 transition-colors">
                            <input
                              type="checkbox"
                              className="w-4 h-4 cursor-pointer accent-blue-600 pointer-events-none"
                              checked={!!sp.selectBox}
                              disabled={!(Number(GrandTotal) > 0)}
                              onChange={(e) => {
                                handleChange(optionIndex, "selectBox", e.target.checked, isAllOptions, indeX, spIdx);
                              }}
                            />
                          </div>
                        </label>
                      </td>
                    ) : (
                      <td className="p-[7px] text-left text-gray-800 font-medium sticky left-0 bg-white z-10">
                        {/* The customer gets the route, not our internal routing key.
                            This cell used to print "OP8-LHR-VIS567-SIN-PTS" into the
                            quotation: our agent code, the handover airport, the
                            airline's internal code and the screening type we chose.
                            None of it means anything to a customer, and it discloses
                            which carrier and haulier we buy from. */}
                        <span className="text-gray-500 text-left">
                          {option.origin} → {Destination || option.destination}
                        </span>
                      </td>
                    )}
                    <td className="p-[7px] text-left font-medium !w-[140px] text-slate-700 dark:text-slate-200">
                      {option.airline?.name}
                      <span className="text-xs block text-slate-400 dark:text-slate-500">
                        ({option.airline?.airlineCode})
                      </span>
                      {/* An ad-hoc rate has no saved record behind it to
                          recompute from — the operator IS the source. So
                          staleness is answered by disclosure: say when it was
                          typed, and nudge once it is old enough to be worth a
                          second look. Never silently re-offer it as if live. */}
                      {option.isAdHoc && !isCustomerMode && (
                        <span className="mt-0.5 block text-[10px] leading-tight text-slate-400 dark:text-slate-500">
                          Rate entered{" "}
                          {option.enteredAt
                            ? new Date(option.enteredAt).toLocaleDateString(undefined, {
                              day: "numeric",
                              month: "short",
                            })
                            : "manually"}
                          {isAdHocRateStale(option.enteredAt) && (
                            <span className="mt-0.5 block font-medium text-amber-600 dark:text-amber-400">
                              Check this rate is still current
                            </span>
                          )}
                        </span>
                      )}
                      {sp.destinationAirport} to {Destination}
                      {/* When this rate lapses, and what it was quoted in.
                          Internal only — a customer is shown a price, not the
                          shelf life of our supplier's rate. */}
                      {!isCustomerMode && (() => {
                        const validity = rateValidity(option.airline?.rateValidityUntil);
                        const fx = foreignCurrency(option.airline?.currency);
                        if (!validity && !fx) return null;
                        return (
                          <span className="mt-1 flex flex-wrap items-center gap-1">
                            {validity && (
                              <span
                                className={
                                  validity.expired
                                    ? "rounded px-1 py-px text-[9.5px] font-semibold uppercase tracking-wide bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300"
                                    : "rounded px-1 py-px text-[9.5px] font-medium text-slate-500 dark:text-slate-400"
                                }
                                title={
                                  validity.expired
                                    ? "This rate has lapsed — re-request before quoting it"
                                    : "Rate validity"
                                }
                              >
                                {validity.expired
                                  ? `Expired ${validity.label}`
                                  : `Valid to ${validity.label}`}
                              </span>
                            )}
                            {fx && (
                              <span
                                className="rounded bg-amber-50 px-1 py-px text-[9.5px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                                title={`Quoted in ${fx} — not converted; the £ total treats it as sterling`}
                              >
                                {fx}
                              </span>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="p-[7px] text-center text-black">{CW}</td>
                    {!isCustomerMode ? (
                      <>
                        <td className="p-[7px] text-center text-slate-400 dark:text-slate-500">
                          {Number(option.airline?.ratePerVolumeWeight || 0).toFixed(2)}
                        </td>
                        <td className="p-[7px] text-center text-slate-400 dark:text-slate-500">
                          {typeof option.airline?.FFWH?.warRiskSurcharge === "object"
                            ? option.airline?.FFWH?.warRiskSurcharge?.ratePerVolumeWeight?.toFixed(2)
                            : option.airline?.FFWH?.warRiskSurcharge || "NA"}
                        </td>
                        <td className="p-[7px] text-center text-slate-400 dark:text-slate-500">
                          {typeof option.airline?.FFWH?.handlingFee === "object"
                            ? option.airline?.FFWH?.handlingFee?.ratePerVolumeWeight?.toFixed(2)
                            : option.airline?.FFWH?.handlingFee || "NA"}
                        </td>
                        <td className="p-[7px] text-center text-slate-400 dark:text-slate-500">
                          {/* Its two siblings above both guard for the object
                              form; this one did not, so any airline whose
                              fuelSurcharge is stored as {ratePerVolumeWeight}
                              — the shape every other read here anticipates —
                              threw "Objects are not valid as a React child"
                              and took the entire cost table down. */}
                          {typeof option.airline?.FFWH?.fuelSurcharge === "object"
                            ? option.airline?.FFWH?.fuelSurcharge?.ratePerVolumeWeight ?? "NA"
                            : option.airline?.FFWH?.fuelSurcharge || "NA"}
                        </td>
                        <td className="p-[5px] text-center border-0">
                          <div className="flex flex-col gap-y-1.5 items-center justify-center w-full">
                            <div className="flex gap-x-1.5 justify-center">
                              <Tooltip content="PTS — Primary Transport Screening (flat £)" position="top">
                                <motion.div
                                  onClick={() =>
                                    !isFinalizedQuote &&
                                    !saveThisTable &&
                                    displayPrimary > 0 &&
                                    onScreeningSelectChange(displayPrimary, option.airline?.airlineCode, option.agentCode, sp.destinationAirport, "PTS")
                                  }
                                  className={`p-2 rounded-lg text-sm transition-all duration-200 min-w-[60px] flex flex-col items-center justify-center
                              ${(displayPrimary === 0 || (isFinalizedQuote && selectedForRow?.type !== "PTS") || saveThisTable) &&
                                    "!cursor-not-allowed opacity-50"}
                                ${!isFinalizedQuote && !saveThisTable && displayPrimary > 0 && "cursor-pointer"}`}
                                  style={{
                                    backgroundColor: selectedForRow?.type === "PTS"
                                      ? 'rgba(0,168,255,0.85)'
                                      : (displayPrimary === 0 || (isFinalizedQuote && selectedForRow?.type !== "PTS") || saveThisTable)
                                        ? (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)')
                                        : (isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.5)'),
                                    border: selectedForRow?.type === "PTS"
                                      ? '1px solid rgba(0,168,255,0.5)'
                                      : '1px solid rgba(200,200,200,0.3)',
                                    boxShadow: selectedForRow?.type === "PTS"
                                      ? '0 2px 8px rgba(0,168,255,0.4)'
                                      : 'none'
                                  }}
                                  whileHover={
                                    (displayPrimary === 0 || (isFinalizedQuote && selectedForRow?.type !== "PTS") || saveThisTable)
                                      ? {}
                                      : { scale: 1.05, boxShadow: '0 4px 12px rgba(0,168,255,0.3)' }
                                  }
                                  whileTap={
                                    (displayPrimary === 0 || (isFinalizedQuote && selectedForRow?.type !== "PTS") || saveThisTable)
                                      ? {}
                                      : { scale: 0.95 }
                                  }
                                >
                                  <div className={`font-semibold text-[12px] ${selectedForRow?.type === "PTS" ? "text-white" : "text-slate-700 dark:text-slate-200"}`}>
                                    PTS
                                  </div>
                                  <div className={`text-[12px] ${selectedForRow?.type === "PTS" ? "text-white" : "text-slate-400 dark:text-slate-500"}`}>
                                    {Number(displayPrimary || 0).toFixed(2)}
                                  </div>
                                </motion.div>
                              </Tooltip>
                              <Tooltip content="STS — Secondary Transport Screening (flat £)" position="top">
                                <motion.div
                                  onClick={() =>
                                    !isFinalizedQuote &&
                                    !saveThisTable &&
                                    displaySecondary > 0 &&
                                    onScreeningSelectChange(displaySecondary, option.airline?.airlineCode, option.agentCode, sp.destinationAirport, "STS")
                                  }
                                  className={`p-2 rounded-lg text-sm transition-all duration-200 min-w-[60px] flex flex-col items-center justify-center
                              ${(displaySecondary === 0 || (isFinalizedQuote && selectedForRow?.type !== "STS") || saveThisTable) &&
                                    "!cursor-not-allowed opacity-50"}
                              ${!isFinalizedQuote && !saveThisTable && displaySecondary > 0 && "cursor-pointer"}`}
                                  style={{
                                    backgroundColor: selectedForRow?.type === "STS"
                                      ? 'rgba(0,168,255,0.85)'
                                      : (displaySecondary === 0 || (isFinalizedQuote && selectedForRow?.type !== "STS") || saveThisTable)
                                        ? (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)')
                                        : (isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.5)'),
                                    border: selectedForRow?.type === "STS"
                                      ? '1px solid rgba(0,168,255,0.5)'
                                      : '1px solid rgba(200,200,200,0.3)',
                                    boxShadow: selectedForRow?.type === "STS"
                                      ? '0 2px 8px rgba(0,168,255,0.4)'
                                      : 'none'
                                  }}
                                  whileHover={
                                    (displaySecondary === 0 || (isFinalizedQuote && selectedForRow?.type !== "STS") || saveThisTable)
                                      ? {}
                                      : { scale: 1.05, boxShadow: '0 4px 12px rgba(0,168,255,0.3)' }
                                  }
                                  whileTap={
                                    (displaySecondary === 0 || (isFinalizedQuote && selectedForRow?.type !== "STS") || saveThisTable)
                                      ? {}
                                      : { scale: 0.95 }
                                  }
                                >
                                  <div className={`font-semibold text-[12px] ${selectedForRow?.type === "STS" ? "text-white" : "text-slate-700 dark:text-slate-200"}`}>
                                    STS
                                  </div>
                                  <div className={`text-[12px] ${selectedForRow?.type === "STS" ? "text-white" : "text-slate-400 dark:text-slate-500"}`}>
                                    {Number(displaySecondary || 0).toFixed(2)}
                                  </div>
                                </motion.div>
                              </Tooltip>
                            </div>
                            <div className="flex gap-x-1.5 justify-center">
                              <Tooltip content="PAS — Primary Airline Screening (per kg)" position="bottom">
                                <motion.div
                                  onClick={() =>
                                    !isFinalizedQuote &&
                                    !saveThisTable &&
                                    pasVal > 0 &&
                                    onScreeningSelectChange(pasVal, option.airline?.airlineCode, option.agentCode, sp.destinationAirport, "PAS")
                                  }
                                  className={`p-2 rounded-lg transition-all duration-200 min-w-[60px] flex flex-col items-center justify-center
                              ${(pasVal === 0 || (isFinalizedQuote && selectedForRow?.type !== "PAS") || saveThisTable) &&
                                    "!cursor-not-allowed opacity-50"}
                                ${!isFinalizedQuote && !saveThisTable && pasVal > 0 && "cursor-pointer"}`}
                                  style={{
                                    backgroundColor: selectedForRow?.type === "PAS"
                                      ? 'rgba(0,168,255,0.85)'
                                      : (pasVal === 0 || (isFinalizedQuote && selectedForRow?.type !== "PAS") || saveThisTable)
                                        ? (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)')
                                        : (isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.5)'),
                                    border: selectedForRow?.type === "PAS"
                                      ? '1px solid rgba(0,168,255,0.5)'
                                      : '1px solid rgba(200,200,200,0.3)',
                                    boxShadow: selectedForRow?.type === "PAS"
                                      ? '0 2px 8px rgba(0,168,255,0.4)'
                                      : 'none'
                                  }}
                                  whileHover={
                                    (pasVal === 0 || (isFinalizedQuote && selectedForRow?.type !== "PAS") || saveThisTable)
                                      ? {}
                                      : { scale: 1.05, boxShadow: '0 4px 12px rgba(0,168,255,0.3)' }
                                  }
                                  whileTap={
                                    (pasVal === 0 || (isFinalizedQuote && selectedForRow?.type !== "PAS") || saveThisTable)
                                      ? {}
                                      : { scale: 0.95 }
                                  }
                                >
                                  <div className={`font-semibold text-[12px] ${selectedForRow?.type === "PAS" ? "text-white" : "text-slate-700 dark:text-slate-200"}`}>
                                    PAS
                                  </div>
                                  <div className={`text-[12px] ${selectedForRow?.type === "PAS" ? "text-white" : "text-slate-400 dark:text-slate-500"}`}>
                                    {Number(pasVal || 0).toFixed(2)}
                                  </div>
                                </motion.div>
                              </Tooltip>
                              <Tooltip content="SAS — Secondary Airline Screening (per kg)" position="bottom">
                                <motion.div
                                  onClick={() =>
                                    !isFinalizedQuote &&
                                    !saveThisTable &&
                                    sasVal > 0 &&
                                    onScreeningSelectChange(sasVal, option.airline?.airlineCode, option.agentCode, sp.destinationAirport, "SAS")
                                  }
                                  className={`p-2 rounded-lg transition-all duration-200 min-w-[60px] flex flex-col items-center justify-center
                              ${(sasVal === 0 || (isFinalizedQuote && selectedForRow?.type !== "SAS") || saveThisTable) &&
                                    "!cursor-not-allowed opacity-50"}
                              ${!isFinalizedQuote && !saveThisTable && sasVal > 0 && "cursor-pointer"}`}
                                  style={{
                                    backgroundColor: selectedForRow?.type === "SAS"
                                      ? 'rgba(0,168,255,0.85)'
                                      : (sasVal === 0 || (isFinalizedQuote && selectedForRow?.type !== "SAS") || saveThisTable)
                                        ? (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)')
                                        : (isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.5)'),
                                    border: selectedForRow?.type === "SAS"
                                      ? '1px solid rgba(0,168,255,0.5)'
                                      : '1px solid rgba(200,200,200,0.3)',
                                    boxShadow: selectedForRow?.type === "SAS"
                                      ? '0 2px 8px rgba(0,168,255,0.4)'
                                      : 'none'
                                  }}
                                  whileHover={
                                    (sasVal === 0 || (isFinalizedQuote && selectedForRow?.type !== "SAS") || saveThisTable)
                                      ? {}
                                      : { scale: 1.05, boxShadow: '0 4px 12px rgba(0,168,255,0.3)' }
                                  }
                                  whileTap={
                                    (sasVal === 0 || (isFinalizedQuote && selectedForRow?.type !== "SAS") || saveThisTable)
                                      ? {}
                                      : { scale: 0.95 }
                                  }
                                >
                                  <div className={`font-semibold text-[12px] ${selectedForRow?.type === "SAS" ? "text-white" : "text-slate-700 dark:text-slate-200"}`}>
                                    SAS
                                  </div>
                                  <div className={`text-[12px] ${selectedForRow?.type === "SAS" ? "text-white" : "text-slate-400 dark:text-slate-500"}`}>
                                    {Number(sasVal || 0).toFixed(2)}
                                  </div>
                                </motion.div>
                              </Tooltip>
                            </div>
                          </div>
                        </td>
                        <td className="p-[7px] text-center text-slate-400 dark:text-slate-500">
                          £ {option.transportCost}
                        </td>
                        <td className="p-[7px] text-center text-slate-400 dark:text-slate-500">
                          {saveThisTable ? (
                            <span className="text-slate-700 dark:text-slate-200">{sp.profitMargin || "0"}</span>
                          ) : (
                            <input
                              type="number"
                              value={sp.profitMargin || 0}
                              onWheel={blurOnWheel}
                              onChange={(e) => {
                                // console.log("profitMargin", e.target.value)
                                handleChange(optionIndex, "profitMargin", e.target.value, true, indeX, spIdx)
                              }}
                              disabled={saveThisTable}
                              className="w-[80px] rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] text-center text-slate-700 outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200"
                            />
                          )}
                        </td>
                        <td className="p-[7px] text-center text-slate-400 dark:text-slate-500">
                          {saveThisTable ? (
                            <span className="text-slate-700 dark:text-slate-200">{sp.custom || "0"}</span>
                          ) : (
                            <input
                              type="number"
                              value={sp.custom || 0}
                              onWheel={blurOnWheel}
                              onChange={(e) => handleChange(optionIndex, "custom", e.target.value, true, indeX, spIdx)}
                              disabled={saveThisTable}
                              className="w-[80px] rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] text-center text-slate-700 outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200"
                            />
                          )}
                        </td>
                        <td className="p-[7px] text-center text-slate-400 dark:text-slate-500">
                          {saveThisTable ? (
                            <span className="text-slate-700 dark:text-slate-200">{sp.extra || "0"}</span>
                          ) : (
                            <input
                              type="number"
                              value={sp.extra || 0}
                              onWheel={blurOnWheel}
                              onChange={(e) => handleChange(optionIndex, "extra", e.target.value, true, indeX, spIdx)}
                              disabled={saveThisTable}
                              className="w-[80px] rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] text-center text-slate-700 outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200"
                            />
                          )}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="p-[7px] text-center text-slate-400 dark:text-slate-500">
                          {option.origin}
                        </td>
                        <td className="p-[7px] text-center text-slate-400 dark:text-slate-500">
                          {/* The SHIPMENT's destination, not sp.destinationAirport.
                              That field is the airport the TRUCKER delivers to — the
                              departure airport — and a row only exists when it matches
                              the airline's origin, so it always equalled Origin. The
                              customer was sent a table reading "LHR -> LHR" for a
                              LHR->SIN shipment, on the same row whose airline cell
                              said "LHR to SIN". */}
                          {Destination || option.destination}
                        </td>
                        <td className="p-[7px] text-center text-slate-400 dark:text-slate-500">
                          {formatTransit(option?.airline?.transitTime)}
                        </td>
                      </>
                    )}
                    {(() => {
                      const selKey = `${option.airline?.airlineCode}_${option.agentCode}_${sp.destinationAirport}`;
                      const selected = selectedScreening[selKey];
                      const computed = selected !== undefined ? computeForScreening(sp, selected) : {};
                      const { FinalRate = 0, GrandTotal = 0, lineItems, anyMinApplied } = computed;
                      return (
                        <>
                          <td className="p-[7px] text-center font-semibold text-green-700">£&nbsp;{FinalRate}</td>
                          {/* The total shows its working. An operator could not
                              reproduce £3.96/kg from anything on screen — no
                              breakdown, no tooltip, and no sign that a minimum
                              charge had replaced a per-kg total. On a pricing
                              tool that is the difference between a number you
                              can defend to a customer and one you cannot. */}
                          <td className="p-[7px] text-center font-semibold text-green-700">
                            {lineItems?.length && !isCustomerMode ? (
                              <CostBreakdown
                                lineItems={lineItems}
                                total={GrandTotal}
                                anyMinApplied={anyMinApplied}
                              />
                            ) : (
                              <>£&nbsp;{GrandTotal}</>
                            )}
                          </td>
                        </>
                      );
                    })()}
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>
      {/* THE TERMS THE PRICE IS QUOTED UNDER. The customer view was right to
          strip margin, customs and extras — and then sent a bare figure. A
          price with no scope, no validity and no statement of what is excluded
          is a number, not a quotation, and the buyer is left to guess whether
          collection is in it. Rendered INSIDE the exported block so it travels
          with the table into the email. Internal view keeps its own legend and
          does not need this. */}
      {isCustomerMode && (() => {
        const scopeLabel = SERVICE_SCOPE_LABELS[quoteOptions[0]?.serviceScope];
        const validUntil = earliestValidity(quoteOptions);
        return (
          <div
            data-testid="customer-quote-terms"
            className="border-t border-slate-100 px-4 py-3 text-[11px] leading-relaxed text-slate-600 dark:border-[oklch(0.3_0.02_264)] dark:text-slate-300"
          >
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
              Terms
            </div>
            <ul className="space-y-0.5">
              {scopeLabel && (
                <li>
                  <b>Service:</b> {scopeLabel}.
                </li>
              )}
              <li>
                <b>Chargeable weight</b> is the greater of the actual gross
                weight and the volumetric weight (length × width × height in cm ÷
                6,000), as is standard for air freight.
              </li>
              {validUntil && (
                <li>
                  <b>Valid until</b>{" "}
                  {validUntil.toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                  , subject to space and capacity at the time of booking.
                </li>
              )}
              <li>
                <b>Excludes:</b>{" "}
                {customerExclusions(quoteOptions[0]?.serviceScope).join("; ")}.
              </li>
            </ul>
          </div>
        );
      })()}
    </motion.div>
  );
}

function FinalQuoteCostTable({
  quoteId,
  disabled,
  defaultOpen,
  refetchTrigger,
  tableRefs = NOOP_TABLE_REFS,
  setItemRefs = noop,
  triggerPanel,
  onFullscreen,
  setRefetchTrigger = null,
  isFullscreen,
  noAccordion = false,
  quoteSource,
  autoFullscreen = false,
}) {
  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const loginToken = useSelector((state) => state.reducer.userToken);
  const { isDarkMode } = useDarkMode();
  const [finalQuote, setFinalQuote] = useState([]);
  const [allOtherQuoteOptions, setAllOtherQuoteOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCustomerMode, setIsCustomerMode] = useState(false);
  const [saveThisTable, setSaveThisTable] = useState(false);
  const [showModal, setShowModal] = useState(false);
  /* The operator's answer to being opened full-width, kept across quotes.
     Auto-fullscreen is a DEFAULT, not a mode: once they close it we stop
     opening it for them, and the Fullscreen control keeps working either way.
     Read lazily so a blocked/absent localStorage cannot break the table. */
  const [fullscreenDeclined, setFullscreenDeclined] = useState(() => {
    try {
      return window.localStorage.getItem(FULLSCREEN_OPT_OUT_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [printPending, setPrintPending] = useState(false);
  const [generateOrNot, setGenerate] = useState(false);
  const [allAirlines, setAllAirlines] = useState([])
  const [allHaulers, setaAllHaulers] = useState([])
  const [vantRefId, setVantRefId] = useState({
    companyName: "VANT",
    userName: "",
    Year: "",
    Month: "",
    sequenceNumber: 1
  });
  const [lastInsertedValue, setLastInsertedValue] = useState(0);
  const [isVantRefIdValid, setIsVantRefIdValid] = useState(false);
  const [vantCopied, setVantCopied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const toggleSaveThisTable = () => {
    setSaveThisTable(!saveThisTable);
  };


  // Generate VANT Ref ID components
  const generateVantRefIdComponents = () => {
    const userName = userLoggedIn?.reg_u_name || userLoggedIn?.name || "";
    const currentDate = new Date();
    const year = currentDate.getFullYear().toString().slice(-2); // Last 2 digits of year
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0'); // Month with leading zero

    // Extract initials of username (e.g. Saksham Sharma -> SS) or first 2 chars
    const nameParts = userName.trim().split(/\s+/);
    const formattedUserName = nameParts.length >= 2
      ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
      : userName.replace(/\s+/g, '').toUpperCase().substring(0, 2);

    return {
      companyName: "VANT", // Always 4 characters
      userName: formattedUserName || "USER",
      Year: year,
      Month: month,
      sequenceNumber: lastInsertedValue + 1
    };
  };

  // Generate complete VANT Ref ID string
  const generateVantRefIdString = (components = vantRefId) => {
    return `${components.companyName}${components.userName}${components.Year}${components.Month}${components.sequenceNumber}`;
  };

  // Validate VANT Ref ID
  const validateVantRefId = (components) => {
    if (!components.userName || !components.Year || !components.Month || !components.sequenceNumber) {
      return false;
    }

    // Check if sequence number is greater than last inserted value
    return components.sequenceNumber > lastInsertedValue;
  };

  // Handle VANT Ref ID input changes
  const handleVantRefIdChange = (field, value) => {
    setVantRefId({ ...vantRefId, [field]: value });
  };

  // Copy generated VANT Ref ID to clipboard
  const handleCopyVantRef = () => {
    const text = generateVantRefIdString();
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setVantCopied(true);
        setTimeout(() => setVantCopied(false), 1500);
      }).catch(() => {
        // no-op fallback
      });
    }
  };

  // Initialize VANT Ref ID when component mounts or user data changes
  useEffect(() => {
    if (userLoggedIn?.reg_u_name || userLoggedIn?.name) {
      const components = generateVantRefIdComponents();
      setVantRefId(components);
    }
  }, [userLoggedIn, lastInsertedValue]);

  // Fetch last/next serial number for VANT Ref and set sequenceNumber accordingly
  useEffect(() => {
    const fetchVantRefs = async () => {
      try {
        const res = await getAllVantRefs({ token: loginToken, role: userLoggedIn?.role });
        if (res && typeof res.nextSerialNumber === 'number') {
          setLastInsertedValue(Number(res.lastSerialNumber || 0));
          setVantRefId(prev => ({ ...prev, sequenceNumber: Number(res.nextSerialNumber) }));
        }
      } catch (e) {
        // swallow error, user can still manually set sequence
      }
    };
    if (loginToken) fetchVantRefs();
  }, [loginToken, userLoggedIn?.role]);

  // Derive validity whenever vantRefId or lastInsertedValue changes — single source of truth
  useEffect(() => {
    setIsVantRefIdValid(validateVantRefId(vantRefId));
  }, [vantRefId, lastInsertedValue]);
  const handleGenerateCustomerTable = () => {
    setIsCustomerMode(true);
  };
  const handleBackToInternalTable = () => {
    setIsCustomerMode(false);
  };
  const printAreaRef = useRef(null);

  const customerExportInlineId = `fqct-customer-export-inline-${quoteId}`;
  const customerExportModalId = `fqct-customer-export-modal-${quoteId}`;

  const copyFinalizedTableToClipboard = async (successToast) => {
    const modalEl = typeof document !== "undefined" ? document.getElementById(customerExportModalId) : null;
    const inlineEl = typeof document !== "undefined" ? document.getElementById(customerExportInlineId) : null;
    const el = showModal ? modalEl : inlineEl;
    const html = el?.innerHTML;
    if (!html || !String(html).trim()) {
      toast.error(
        showModal
          ? "Nothing to copy in this view."
          : "Open “Project Full View Combination Table” from here, or use this copy when the finalized table is shown above."
      );
      return;
    }
    const plain = (el.innerText && el.innerText.trim()) ? el.innerText : html.replace(/<[^>]*>/g, "");
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      toast.success(successToast);
    } catch {
      try {
        await navigator.clipboard.writeText(plain);
        toast.success("Copied as plain text.");
      } catch {
        toast.error("Could not copy to clipboard.");
      }
    }
  };

  const handleCopyForGmail = () =>
    copyFinalizedTableToClipboard("Copied for Gmail (rich HTML where your client supports it).");

  const handleCopyCustomerView = () =>
    copyFinalizedTableToClipboard("Customer view copied — paste into your email.");

  /**
   * Build a self-contained, email-safe HTML representation of the
   * finalised customer table. All styling is inlined (no Tailwind, no
   * external classes) so it renders identically in Gmail, Outlook,
   * Apple Mail, etc. — the previous DOM-clone approach lost all styling
   * the moment Tailwind utility classes left the React app.
   *
   * Source of truth is finalQuote[0] (set by fetchQuoteDetails from the
   * server's finalizedQuoteOption.calculationDataForReference), so the
   * rendered table is always the canonical post-finalise numbers.
   */
  const buildEmailHtmlForFinalised = () => {
    if (!finalQuote?.length) return { html: "", plain: "" };
    const opt = finalQuote[0] || {};
    const sp = opt?.screeningPrices?.[0] || {};
    const calc = opt?.dataForCalculation || {};
    const airline = opt?.airline || {};
    const airlineName = airline?.name || "";
    const airlineCode = airline?.airlineCode || "";
    const destinationAirport = sp?.destinationAirport || calc?.destinationAirport || "";
    const finalDestination = opt?.destination || "";
    const origin = opt?.origin || calc?.from || "";
    const transitTime = airline?.transitTime || "";
    const CW = opt?.cumulativeVolumeWeight || calc?.CW || "";
    const finalRate = calc?.finalRate || "";
    const grandTotal = calc?.GrandTotal || "";
    const vantRef = opt?.vantRef || "";
    /* The route, not our internal routing key. This built
       "<agentCode>-<handoverAirport>-<airlineCode>" and printed it into the
       customer's quotation, disclosing which haulier and carrier we buy from.
       The VANT Ref below is the reference a customer should quote back to us. */
    const idStr =
      [origin, finalDestination].filter(Boolean).join(" → ") || "—";

    const esc = (v) =>
      String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    // Reusable cell styles — Gmail/Outlook collapse classes, only inline
    // styles survive.
    const cellBase =
      "border: 1px solid #e2e8f0; padding: 10px 12px; font-family: Arial, Helvetica, sans-serif; font-size: 13px; vertical-align: middle;";
    const headerCell = `${cellBase} background-color: #f1f5f9; color: #475569; font-weight: 600; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px;`;
    const ratesHeaderCell = `${headerCell} background-color: #ecfdf5; color: #047857; border-left: 1px solid #a7f3d0;`;
    const ratesCell = `${cellBase} background-color: #ecfdf5; color: #047857; font-weight: 700; text-align: right; border-left: 1px solid #a7f3d0;`;

    const html = `
<div style="font-family: Arial, Helvetica, sans-serif; color: #0f172a;">
  <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse: collapse; min-width: 560px; border: 1px solid #cbd5e1;">
    <thead>
      <tr>
        <th style="${headerCell} text-align: center;">ID</th>
        <th style="${headerCell} text-align: left;">Airline</th>
        <th style="${headerCell} text-align: right;">CW (kg)</th>
        <th style="${headerCell} text-align: center;">Origin</th>
        <th style="${headerCell} text-align: center;">Destination</th>
        <th style="${headerCell} text-align: center;">Transit</th>
        <th style="${ratesHeaderCell} text-align: right;">Final Rate £/kg</th>
        <th style="${ratesHeaderCell} text-align: right;">Total £</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="${cellBase} text-align: center; color: #475569; font-family: 'Courier New', Courier, monospace; font-size: 11px;">${esc(idStr)}</td>
        <td style="${cellBase} text-align: left;">
          <div style="font-weight: 600; color: #0f172a;">${esc(airlineName)}</div>
          ${airlineCode ? `<div style="color: #94a3b8; font-size: 11px;">(${esc(airlineCode)})</div>` : ""}
          ${destinationAirport || finalDestination ? `<div style="color: #64748b; font-size: 11px;">${esc(destinationAirport)} to ${esc(finalDestination)}</div>` : ""}
        </td>
        <td style="${cellBase} text-align: right; font-weight: 600;">${esc(CW)}</td>
        <td style="${cellBase} text-align: center; color: #475569;">${esc(origin) || "—"}</td>
        <!-- The SHIPMENT's destination. destinationAirport is where the TRUCKER
             delivers (the departure airport), so this column read "LHR" for a
             LHR->SIN shipment in emails already sent to customers. -->
        <td style="${cellBase} text-align: center; color: #475569;">${esc(finalDestination || destinationAirport) || "—"}</td>
        <td style="${cellBase} text-align: center; color: #475569;">${esc(formatTransit(transitTime))}</td>
        <td style="${ratesCell}">£&nbsp;${esc(finalRate)}</td>
        <td style="${ratesCell}">£&nbsp;${esc(grandTotal)}</td>
      </tr>
    </tbody>
  </table>
  ${vantRef ? `<div style="margin-top: 14px; font-family: Arial, Helvetica, sans-serif; font-size: 13px; color: #475569;"><strong style="color: #1e293b;">VANT Ref:</strong> <span style="font-family: 'Courier New', Courier, monospace; color: #0f172a;">${esc(vantRef)}</span></div>` : ""}
</div>`.trim();

    const plain = [
      `ID: ${idStr}`,
      `Airline: ${airlineName}${airlineCode ? ` (${airlineCode})` : ""}${destinationAirport || finalDestination ? ` — ${destinationAirport} to ${finalDestination}` : ""}`,
      `CW: ${CW} kg`,
      `Origin: ${origin || "—"}`,
      `Destination: ${destinationAirport || "—"}`,
      `Transit: ${transitTime || "—"}`,
      `Final Rate: £${finalRate}/kg`,
      `Total: £${grandTotal}`,
      vantRef ? `VANT Ref: ${vantRef}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    return { html, plain };
  };

  /**
   * Copy the finalised customer-view table as email-safe HTML (inline
   * styles) so it pastes cleanly into Gmail / Outlook / Apple Mail.
   * Manual-query users have no Customer Conversation side panel, so this
   * is their only path to send the finalised option to the customer.
   */
  const handleCopyFinalisedForEmail = async () => {
    if (!finalQuote?.length || !finalQuote[0]?.agentCode) {
      toast.error("Finalise an option first, then copy.");
      return;
    }
    const { html, plain } = buildEmailHtmlForFinalised();
    if (!html) {
      toast.error("No finalised table data to copy.");
      return;
    }
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      toast.success("Customer table copied — paste into Gmail / Outlook.");
    } catch {
      try {
        await navigator.clipboard.writeText(plain);
        toast.success("Copied as plain text.");
      } catch {
        toast.error("Could not copy to clipboard.");
      }
    }
  };

  const doPrint = () => {
    const printContent = printAreaRef.current;
    if (!printContent) return;

    const styles = Array.from(document.styleSheets)
      .map(sheet => {
        try { return Array.from(sheet.cssRules).map(r => r.cssText).join('\n'); }
        catch { return ''; }
      })
      .join('\n');

    const newWindow = window.open('', '_blank', 'width=1000,height=800');
    newWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>Quote Table</title>
    <style>${styles}</style>
  </head>
  <body class="${document.body.className}" style="background:#fff;padding:16px;">
    ${printContent.innerHTML}
  </body>
</html>`);
    newWindow.document.close();
    newWindow.focus();
    newWindow.print();
    newWindow.close();
  };

  /* Export the priced table as an image or an A4 PDF. Fully client-side —
     no finalise step, no persisted quote, nothing to wait for. The operator
     asked to "just send a screenshot"; this is that, minus the OS. */
  const handleCopyImage = async () => {
    if (!showModal) {
      setShowModal(true);
      return;
    }
    const { ok, reason } = await copyElementAsPng(printAreaRef.current);
    if (ok) toast.success("Cost table copied — paste it into your email.");
    else toast.error(reason || "Could not copy the table.");
  };

  const handleDownloadPdf = async () => {
    if (!showModal) {
      setShowModal(true);
      return;
    }
    const name = `quotation-${quoteId || "freightkit"}.pdf`;
    const { ok, reason } = await downloadElementAsPdf(printAreaRef.current, name);
    if (!ok) toast.error(reason || "Could not build the PDF.");
  };

  const handlePrint = () => {
    if (!showModal) {
      setShowModal(true);
      setPrintPending(true);
    } else {
      doPrint();
    }
  };

  useEffect(() => {
    if (printPending && showModal) {
      const raf = requestAnimationFrame(() => {
        setPrintPending(false);
        doPrint();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [printPending, showModal]);

  const modalScrollRef = useRef(null);
  const scrollPositionRef = useRef({ top: 0, left: 0 });

  const handleModalScroll = () => {
    if (modalScrollRef.current) {
      scrollPositionRef.current = {
        top: modalScrollRef.current.scrollTop,
        left: modalScrollRef.current.scrollLeft
      };
    }
  };

  const finalizeANomination = async (e) => {
    e.preventDefault();

    if (!generateOrNot || !Array.isArray(generateOrNot) || generateOrNot.length === 0) {
      return console.warn("No quote found to finalize.");
    }
    const quote = generateOrNot[0];
    if (!quote) return console.warn("No quote found to finalize.");

    const savedAgentId = quote.selectedTransporterId;
    const savedAirlineId = quote.airline.savedAirlineId;

    const screening = quote.dataForCalculation?.SelectedScreening || {};
    const selectedScreening = {
      [quote.dataForCalculation?.ScreeningID]: screening
    };

    const airlineTotal = (quote.dataForCalculation.FR + quote.dataForCalculation.WR + quote.dataForCalculation.HF) * quote.dataForCalculation.CW * (1 + quote.dataForCalculation.FS / 100) + (quote.dataForCalculation?.isFSc ? quote.dataForCalculation?.FSc : 0) * quote.dataForCalculation?.CW;
    const HaulerCost = quote.dataForCalculation.TC + (quote.dataForCalculation?.isTSc ? quote.dataForCalculation.TSc : 0)


    const CW = Number(quote.dataForCalculation?.CW) || 0;
    const airlinePayable = {
      ratePerVolumeWeight: CW > 0 ? (airlineTotal / CW).toFixed(2) : "0.00",
      minCharge: quote.airline.FFWH?.freightCharge?.minCharge || 0,
      total: airlineTotal
    };

    const agentPayable = {
      ratePerVolumeWeight: CW > 0 ? (HaulerCost / CW).toFixed(2) : "0.00",
      minCharge: 0,
      total: HaulerCost
    };

    const totalPayable = {
      ratePerVolumeWeight: Number(quote.dataForCalculation?.finalRate) || 0,
      total: Number(quote.dataForCalculation?.GrandTotal) || 0,
    };

    const finalizedOption = {
      keyID: quote.dataForCalculation?.ScreeningID || "",
      savedAgentId,
      savedAirlineId,
      selectedScreening,
      airlinePayable,
      agentPayable,
      totalPayable,
      calculationDataForReference: { ...quote.dataForCalculation, screeningPrices: quote.screeningPrices.filter((x) => x.destinationAirport === quote.dataForCalculation?.destinationAirport) } || {},
      grandTotal: Number(quote.dataForCalculation?.GrandTotal) || 0,
      /* Margin earned, from the SAME derivation that produced the price.
         This used to be GrandTotal minus (airlinePayable + agentPayable), which
         subtracted one formula's cost from another formula's revenue: the
         payables exclude customs and screening, the total includes them. On the
         reported quote it recorded £337.54 against a real margin of £232.06 —
         overstating profit by 45% — and this figure is the "Expected profit"
         series on the operator dashboard.

         baseCost is exactly the number the margin percentage was applied to, so
         GrandTotal - baseCost is the margin plus the flat extra, and nothing
         else. Supplier cost is never re-derived a second way. */
      expectedProfit:
        Number(quote.dataForCalculation?.GrandTotal || 0) -
        Number(quote.dataForCalculation?.baseCost || 0),
      mediatorAirport: quote.dataForCalculation?.destinationAirport || null,
      finalizedAt: new Date(),
    };
    try {
      let finalCheck = {
        vantRef: generateVantRefIdString(),
        ...finalizedOption,
        calculationDataForReference: { ...quote, screeningPrices: quote.screeningPrices.filter((x) => x.destinationAirport === quote.dataForCalculation?.destinationAirport) }
      }
      if (isObjectEmpty(finalCheck.selectedScreening[finalCheck.keyID])) {
        showSwal("Error", "Selected Option Screening is empty", 400);
        return;
      }
      await finalizeAQuoteNomination({
        token: loginToken,
        id: quoteId,
        role: userLoggedIn?.role,
        payload: {
          vantRef: generateVantRefIdString(),
          ...finalizedOption,
          calculationDataForReference: { ...quote, screeningPrices: quote.screeningPrices.filter((x) => x.destinationAirport === quote.dataForCalculation?.destinationAirport) }
        },
      });

      // Update last inserted value after successful finalization
      setLastInsertedValue(vantRefId.sequenceNumber);
      if (setRefetchTrigger) {
        setRefetchTrigger(prev => prev + 1);
      }
      toast.success(`Nominated — ${generateVantRefIdString()}`);
    } catch (error) {
      /* A rejected nomination used to die in the console: the button stayed
         enabled, nothing on screen moved, and the operator had no way to tell a
         409 (already finalised / stale) from a dead click. Surface it. */
      console.error("Error finalizing nomination:", error);
      const status = error?.response?.status;
      const detail =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Unknown error";
      showSwal(
        "Could not finalise",
        status === 409
          ? `This quote has already been finalised, or it changed since you opened it. Refresh and try again. (${detail})`
          : detail,
        status || 500
      );
    }
  };

  const [selectedScreening, setSelectedScreening] = useState({});
  /* Saving is armed only AFTER a load has applied any existing draft.
     Without this the first render — options built, draft not yet merged — would
     persist a blank working state over the operator's saved one. State, not a
     ref, because the Save button's own enabled/disabled look depends on it. */
  const [draftArmed, setDraftArmed] = useState(false);
  const [storedDraftSavedAt, setStoredDraftSavedAt] = useState(null);
  const [unsavedPrompt, setUnsavedPrompt] = useState(false);
  /* What the operator has on screen RIGHT NOW, readable from inside the fetch
     without putting it in that effect's deps. Assigned during render (the same
     pattern as useCostTableDraft's `latest`) so a fetch that lands mid-edit sees
     the edit rather than the state the fetch started with. */
  const liveTableRef = useRef({ optionGroups: [], selectedScreening: {} });
  liveTableRef.current = { optionGroups: allOtherQuoteOptions, selectedScreening };
  /* Which quote those options describe. Stamped by the fetch, not by render: a
     DIFFERENT quote arriving is a first load however much the previous one has
     left on screen, or its pricing would be carried across. */
  const loadedQuoteIdRef = useRef(null);

  const onScreeningSelectChange = (screeningPrice, airlineCode, transportCode, destinationAirport, type) => {
    const key = `${airlineCode}_${transportCode}_${destinationAirport}`;
    setSelectedScreening((prev) => ({
      ...prev,
      [key]: { value: screeningPrice, type },
    }));
  };

  const handleDownloadCsv = () => {
    // Build from the real in-scope options — the finalized winner (finalQuote)
    // plus the other combinations. (The old code referenced quoteOptions /
    // calculateFinalRate2, neither of which exists in this scope, so the button
    // threw a ReferenceError on click.)
    /* allOtherQuoteOptions is an array of option GROUPS (one per transport
       agent), not options. Spreading it handed buildCostTableCsv arrays, whose
       .airline/.dataForCalculation are undefined — so the file downloaded with
       a header and one blank row per group. Flatten to real options. */
    const options = [
      ...(finalQuote || []),
      ...(allOtherQuoteOptions || []).flat(),
    ];
    const csvContent = buildCostTableCsv(options);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "customer_quote_table.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleChange = (optionIndex, field, value, isAllOptions, indeX, idX = null) => {
    const numericValue = typeof value === "boolean" ? value : parseFloat(value) || 0;
    const applyUpdate = (optionsArray, index) => {
      if (idX !== null) {
        /* Rows are rendered from a FILTERED view of screeningPrices, so callers
           must resolve the row against the UNFILTERED array written to here (see
           spIdx at the call sites) — a position taken from the filtered view
           addresses a different destination entirely.

           A row that cannot be resolved is not written to. Falling back to any
           other position is how the operator's margin reached a destination
           they never picked, and doing nothing is the only safe alternative:
           there is no second guess that is not another guess. */
        const targetRow = optionsArray[optionIndex]?.screeningPrices?.[idX];
        if (!targetRow) return;
        if (typeof value === "boolean") {
          targetRow[field] = value;
        } else {
          targetRow[field] = numericValue < 0 ? 0 : numericValue;
        }
      } else {
        const targetIndex = optionIndex;
        if (targetIndex !== null) {
          optionsArray[targetIndex][field] = numericValue < 0 ? 0 : numericValue;
        } else {
          optionsArray.forEach((option) => {
            if (field == "profitMargin" || field == "custom" || field == "extra" || field == "selectBox") {
              option.screeningPrices.forEach((sp) => {
                sp[field] = typeof value === "boolean" ? value : (numericValue < 0 ? 0 : numericValue);
              });
            } else {
              option[field] = numericValue < 0 ? 0 : numericValue;
            }
          });
        }
      }
    };
    if (isAllOptions) {
      setAllOtherQuoteOptions((prev) => {
        const newOptions = JSON.parse(JSON.stringify(prev));
        if (indeX === "all") {
          newOptions.forEach((group, index) => applyUpdate(group, index));
        } else if (typeof indeX === "number") {
          applyUpdate(newOptions[indeX]);
        }
        return newOptions;
      });
    }
  };

  useEffect(() => {
    const fetchQuoteDetails = async () => {
      if (!quoteId || !loginToken) {
        setLoading(false);
        setError("Quote ID or login token is missing.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await getAQuoteById({ loginToken, role: userLoggedIn?.role, id: quoteId });
        const quotesList = response?.quotes;
        const hasQuotes = Array.isArray(quotesList) && quotesList.length > 0;
        /** API often returns the quote payload without a numeric `status` (axios `res.data`); do not treat that as failure */
        const statusRejected =
          response?.status != null && response.status !== 200 && response.status !== "200";
        if (hasQuotes && !statusRejected) {
          const fetchedQuote = quotesList[0];
          const cumulativeVolumeWeight = fetchedQuote?.containerDetails?.chargeable_volume_weight || 0;
          const airlinesWithRates = fetchedQuote?.my_saved_airlines?.filter(
            (savedAirline) => savedAirline.finalRate !== undefined && savedAirline.finalRate !== null && savedAirline.finalRate > 0
          ) || [];
          const transportWithRates = fetchedQuote?.my_saved_agents?.filter(
            (savedAgent) => savedAgent.finalRate !== undefined && savedAgent.finalRate !== null && savedAgent.finalRate > 0
          ) || [];
          setAllAirlines(airlinesWithRates);
          setaAllHaulers(transportWithRates);
          const savedOptionGroups = transportWithRates.map((transportAgent) => airlinesWithRates.map(savedAirline => ({
            airline: {
              name: savedAirline.airlineDetail?.name || 'N/A',
              airlineCode: savedAirline.airlineDetail?.airlineCode || 'N/A',
              savedAirlineId: savedAirline._id,
              airlineId: savedAirline.airlineDetail?._id || 'N/A',
              transitTime: savedAirline.transitTime || 'N/A',
              ratePerVolumeWeight: parseFloat(savedAirline.finalRate),
              screeningPrices: {
                primary: savedAirline.screeningPrices?.primaryScreeningPrice,
                secondary: savedAirline.screeningPrices?.secondaryScreeningPrice,
              },
              FFWH: savedAirline.FFWH || {},
              originAirports: savedAirline?.originAirports || [],
              /* Both are stored on the saved airline and both used to be
                 dropped here, so the row could not say when its rate lapsed or
                 what currency it was quoted in. An operator was pricing off a
                 rate with no expiry on screen. */
              rateValidityUntil: savedAirline.rateValidityUntil || null,
              currency: savedAirline.currency || null,
            },
            origin: fetchedQuote?.containerDetails.origin || 'N/A',
            destination: fetchedQuote?.containerDetails.destination || 'N/A',
            // Shipment-level, like origin/destination: what the customer is
            // being quoted FOR. Without it the quotation states a price and
            // leaves the buyer to guess whether haulage is in it.
            serviceScope: fetchedQuote?.containerDetails?.serviceScope || "",
            cumulativeVolumeWeight: cumulativeVolumeWeight,
            transportCost: parseFloat(transportAgent?.finalRate) || 0,
            selectedTransporterId: transportAgent?._id,
            screeningPrices: (Array.isArray(transportAgent.screeningPrices) && transportAgent.screeningPrices.length
              ? transportAgent.screeningPrices.map((x) => ({ profitMargin: 0, extra: 0, custom: 0, ...x, selectBox: false }))
              : [{
                selectBox: false,
                profitMargin: 0,
                extra: 0,
                custom: 0,
                primary: (typeof transportAgent.screeningPrices?.primaryScreeningPrice === "number" || typeof transportAgent.screeningPrices?.primaryScreeningPrice === "string") ? transportAgent.screeningPrices.primaryScreeningPrice : transportAgent.screeningPrices?.primaryScreeningPrice?.ratePerVolumeWeight,
                secondary: (typeof transportAgent.screeningPrices?.secondaryScreeningPrice === "number" || typeof transportAgent.screeningPrices?.secondaryScreeningPrice === "string") ? transportAgent.screeningPrices.secondaryScreeningPrice : transportAgent.screeningPrices?.secondaryScreeningPrice?.ratePerVolumeWeight,
                destinationAirport: transportAgent?.screeningPrices?.destinationAirport || "any"
              }]),
            agentType: transportAgent?.agentDetail?.agentType || "N/A",
            agentCode: transportAgent?.agentDetail?.agentCode || "N/A",
            profitMargin: 10,
            custom: 0,
            extra: 10,
            dataForCalculation: {}
          })));

          /* "I know the rate" options the operator typed from memory, folded in
             alongside the saved cross-product. They are the only reason a quote
             with NO saved airline and NO saved transporter can still produce a
             row — which is the whole point: creating a permanent directory
             entry to price one shipment was the blocker. */
          const otherOptions = mergeAdHocOptions(
            savedOptionGroups,
            fetchedQuote?.adHocOptions,
            {
              cumulativeVolumeWeight,
              origin: fetchedQuote?.containerDetails?.origin || "N/A",
              destination: fetchedQuote?.containerDetails?.destination || "N/A",
              serviceScope: fetchedQuote?.containerDetails?.serviceScope || "",
            }
          );
          // Only update allOtherQuoteOptions if it's empty (initial load) or if we're not in the middle of editing
          // Re-apply the operator's saved working state (screening choice,
          // margin, customs, extra, ticks). Without this the table rebuilt from
          // live rates with hardcoded defaults every time it opened, so the
          // pricing done before sending options to a customer was discarded and
          // had to be retyped to nominate the option they accepted.
          /* Which working state to apply, the stored one or the live one.
             A FIRST load has nothing on screen, so the stored draft IS the
             operator's work. A REFETCH (a rate saved, options sent, an option
             finalised) happens mid-session, by which point the stored draft is
             only a snapshot of what was last written: replaying it would undo
             every change made since. So the live table is lifted off and
             re-applied instead. Either way the OPTIONS are the freshly fetched
             ones, so a rate changed elsewhere shows up — the old code kept the
             previous options wholesale on a refetch, hiding the new rate, while
             still replaying the stored screening over the operator's own. */
          const live = liveTableRef.current;
          const loadedOptionCount = (Array.isArray(live.optionGroups) ? live.optionGroups : [])
            .reduce((n, group) => n + (Array.isArray(group) ? group.length : 0), 0);
          const isFirstLoad = loadedQuoteIdRef.current !== quoteId || loadedOptionCount === 0;
          const workingRows = isFirstLoad
            ? fetchedQuote?.costTableDraft?.rows
            : extractDraftRows(live.optionGroups, live.selectedScreening);
          const restoredScreening =
            Array.isArray(workingRows) && workingRows.length > 0
              ? applyDraftRows(otherOptions, workingRows)
              : {};
          loadedQuoteIdRef.current = quoteId;
          setStoredDraftSavedAt(fetchedQuote?.costTableDraft?.updatedAt || null);
          setDraftArmed(true);
          setAllOtherQuoteOptions(otherOptions);
          const finalizedOpt = fetchedQuote.finalizedQuoteOption;
          const finalizedKeyId = finalizedOpt?.keyID;
          const screeningMap =
            finalizedOpt?.selectedScreening != null && typeof finalizedOpt.selectedScreening === "object"
              ? finalizedOpt.selectedScreening
              : null;
          const screeningForKey =
            screeningMap != null && finalizedKeyId != null ? screeningMap[finalizedKeyId] : undefined;
          let myFinalQuote = filterUndefinedFields({
            ...finalizedOpt?.calculationDataForReference,
            keyID: finalizedKeyId,
            selectedScreening: screeningForKey,
          });
          setSelectedScreening((prev) => {
            /* This state is a MAP keyed by `${airline}_${agent}_${dest}`. It
               previously fell back to a screening PRICE (a number) when nothing
               was finalised, which silently destroyed every screening selection
               on any refetch — and made the saved draft extract to zero rows,
               overwriting a good draft with an empty one. A number was never a
               valid value here, so the fallback is gone entirely. */
            const prevSafe = prev && typeof prev === "object" ? prev : {};
            const fromFinalised = myFinalQuote.selectedScreening
              ? screeningMap ?? {}
              : {};
            /* restoredScreening is the operator's OWN choice — the stored draft
               on a first load, their live choice re-priced against the rates
               that just arrived on a refetch — so it is applied last. */
            return { ...prevSafe, ...fromFinalised, ...restoredScreening };
          });
          setFinalQuote(isObjectEmpty(myFinalQuote) ? [] : [{ ...myFinalQuote, vantRef: fetchedQuote?.vantRef }])
          // Include the finalised option's code in itemRefs so triggerPanel can
          // resolve its DOM ref when the "Send to Customer Conversation" button
          // is clicked from the finalised view — without this, the panel opens
          // with no attached table.
          const finalCode = isObjectEmpty(myFinalQuote)
            ? null
            : (myFinalQuote.agentCode || "") + "_" + (myFinalQuote.screeningPrices?.map((x) => x?.destinationAirport).join("-") || "");
          setItemRefs([
            ...otherOptions.map(allOtherQuoteOption => (allOtherQuoteOption && allOtherQuoteOption.length > 0 ? allOtherQuoteOption[0]?.agentCode : '') + "_" + (allOtherQuoteOption && allOtherQuoteOption.length > 0 ? allOtherQuoteOption[0]?.screeningPrices?.map((x) => x?.destinationAirport).join("-") : "")),
            // Customer-safe twins. "Send options to customer" attaches THESE so
            // Margin %, Customs £, Extra £ and the rate breakdown never reach
            // the customer — the internal refs above carry the full table.
            ...otherOptions.map(allOtherQuoteOption => ((allOtherQuoteOption && allOtherQuoteOption.length > 0 ? allOtherQuoteOption[0]?.agentCode : '') + "_" + (allOtherQuoteOption && allOtherQuoteOption.length > 0 ? allOtherQuoteOption[0]?.screeningPrices?.map((x) => x?.destinationAirport).join("-") : "")) + "__customer"),
            ...(finalCode ? [finalCode, finalCode + "__customer"] : []),
          ]);
        } else {
          setError(response.message || "Failed to fetch quote details.");
          setAllOtherQuoteOptions([]);
          setItemRefs([]);
        }
      } catch (err) {
        console.error("Error fetching quote details:", err);
        setError("An error occurred while fetching quote details.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuoteDetails();
  }, [quoteId, loginToken, userLoggedIn?.role, refetchTrigger]);


  useEffect(() => {
    const selectedOptions = allOtherQuoteOptions.flat().filter(option =>
      option.screeningPrices.map(x => x.selectBox).includes(true)
    );
    const enhancedOptions = selectedOptions.map(option => {
      const selectedScreeningPrice = option.screeningPrices.find(sp => sp.selectBox);
      const CW = option.cumulativeVolumeWeight

      if (selectedScreeningPrice) {
        const selKey = `${option.airline?.airlineCode}_${option.agentCode}_${selectedScreeningPrice.destinationAirport}`;
        const selected = selectedScreening[selKey];
        const computeForScreening = (sp, selected) => {
          const primaryRate = sp.primaryScreeningPrice?.ratePerVolumeWeight ?? sp.primaryScreeningPrice ?? 0;
          const primaryMin = sp.primaryScreeningPrice?.minCharge ?? 0;
          const primaryFinal = Math.max(primaryRate * CW, primaryMin);
          const secondaryRate = sp.secondaryScreeningPrice?.ratePerVolumeWeight ?? sp.secondaryScreeningPrice ?? 0;
          const secondaryMin = sp.secondaryScreeningPrice?.minCharge ?? 0;
          const secondaryFinal = Math.max(secondaryRate * CW, secondaryMin);
          const TPrimaryRate = sp.primary ?? sp.primaryScreeningPrice?.ratePerVolumeWeight ?? 0;
          const TPrimaryMin = sp.primaryScreeningPrice?.minCharge ?? 0;
          const TPrimaryFinal = Math.max(TPrimaryRate * CW, TPrimaryMin);

          const TSecondaryRate = sp.secondary ?? sp.secondaryScreeningPrice?.ratePerVolumeWeight ?? 0;
          const TSecondaryMin = sp.secondaryScreeningPrice?.minCharge ?? 0;
          const TSecondaryFinal = Math.max(TSecondaryRate * CW, TSecondaryMin);
          const selectedValue = selected?.value || 0;
          const isTSc = selected?.type === "PTS" || selected?.type === "STS";
          const isFSc = selected?.type === "PAS" || selected?.type === "SAS";
          const TSc = isTSc ? selectedValue : 0;
          const FSc = isFSc ? selectedValue : 0;

          // Haulage is optional; the air rate is not. Requiring transportCost
          // here made an airport-to-airport quote impossible to price.
          if (!isOptionPriceable(option)) {
            return { dataForCalculation: {} };
          }

          const spPM = sp.profitMargin ?? option.profitMargin ?? 0;
          const spExC = sp.extra ?? option.extra ?? 0;
          const spCF = sp.custom ?? option.custom ?? 0;

          const FR = option.airline?.ratePerVolumeWeight;
          const WR = option.airline?.FFWH?.warRiskSurcharge?.ratePerVolumeWeight ?? option.airline?.FFWH?.warRiskSurcharge ?? 0;
          const HF = option.airline?.FFWH?.handlingFee?.ratePerVolumeWeight ?? option.airline?.FFWH?.handlingFee ?? 0;
          const FS = option.airline?.FFWH?.fuelSurcharge?.ratePerVolumeWeight ?? option.airline?.FFWH?.fuelSurcharge ?? 0;
          const freightFinal = Math.max(FR * CW, option.airline?.FFWH?.freightCharge?.minCharge ?? 0);
          const warRiskFinal = Math.max(WR * CW, option.airline?.FFWH?.warRiskSurcharge?.minCharge ?? 0);
          const handlingFinal = Math.max(HF * CW, option.airline?.FFWH?.handlingFee?.minCharge ?? 0);

          let baseCost = (freightFinal + warRiskFinal + handlingFinal + spCF) *
            (1 + FS / 100) +
            option.transportCost;

          if (isFSc) baseCost += option.cumulativeVolumeWeight * FSc;
          else if (isTSc) baseCost += TSc;

          const profit = (baseCost * spPM) / 100;
          const finalRateNum = baseCost + profit + spExC;

          return {
            dataForCalculation: {
              ScreeningID: `${option.airline?.airlineCode}_${option.agentCode}_${sp.destinationAirport}`,
              SelectedScreening: selectedScreening[`${option.airline?.airlineCode}_${option.agentCode}_${sp.destinationAirport}`],
              CW: option.cumulativeVolumeWeight,
              FR: option.airline?.ratePerVolumeWeight,
              WR: option.airline?.FFWH?.warRiskSurcharge?.ratePerVolumeWeight ?? option.airline?.FFWH?.warRiskSurcharge ?? 0,
              HF: option.airline?.FFWH?.handlingFee?.ratePerVolumeWeight ?? option.airline?.FFWH?.handlingFee ?? 0,
              FS: option.airline?.FFWH?.fuelSurcharge?.ratePerVolumeWeight ?? option.airline?.FFWH?.fuelSurcharge ?? 0,
              TC: option.transportCost,
              PM: spPM,
              CF: spCF,
              ExC: spExC,
              finalRate: option.cumulativeVolumeWeight ? (finalRateNum / option.cumulativeVolumeWeight).toFixed(2) : "N/A",
              GrandTotal: Number(finalRateNum).toFixed(2),
              /* The figure the margin percentage was applied to. finalizeANomination
                 records expectedProfit as GrandTotal - baseCost, and it reads that
                 from THIS object (generateOrNot[0]) — not from the display path's
                 copy, which is the only other place baseCost was set. Omitting it
                 here meant Number(undefined || 0) === 0, so every nomination stored
                 the entire sell price as profit: £2,371.71 recorded against a real
                 margin of £224.70 on the quote we checked. Same derivation as the
                 price, so profit is margin + the flat extra and nothing else. */
              baseCost,
              destinationAirport: sp.destinationAirport,
              selectedAirlineId: option.airline?.airlineId,
              selectedTransporterId: option.selectedTransporterId,
              from: option.origin,
              agentCode: option.agentCode,
              agentType: option.agentType,
            }
          };
        };

        const computed = computeForScreening(selectedScreeningPrice, selected);
        return {
          ...option,
          dataForCalculation: computed.dataForCalculation
        };
      }

      return option;
    });

    setGenerate(enhancedOptions);
  }, [allOtherQuoteOptions, selectedScreening]);

  /* Persistence of the operator's working state — debounced autosave, the
     explicit Save, and the dirty comparison that drives both. All of it lives
     in the hook so a failed write can no longer disappear into console.error
     and a pending one is flushed on unmount rather than cancelled. */
  const draft = useCostTableDraft({
    quoteId,
    loginToken,
    role: userLoggedIn?.role,
    optionGroups: allOtherQuoteOptions,
    selectedScreening,
    armed: draftArmed,
    initialSavedAt: storedDraftSavedAt,
  });

  const savePricing = useCallback(async () => {
    const outcome = await draft.saveNow();
    if (outcome.ok) toast.success("Pricing saved");
    else toast.error(`Could not save: ${outcome.error}`);
    return outcome;
  }, [draft]);

  /* Every way out of the table used to discard silently. The two the operator
     can click now ask first; the third (the lane unmounting) cannot show a
     dialog, so the hook flushes instead. */
  const hasUnsavedWork = draft.status === "dirty" || draft.status === "error";
  /* Pressing Close IS the answer to "should I open this full-width for you" —
     record it so the table never has to be dismissed twice. A click on the
     backdrop is not: it is as often a misclick as a decision, and it should not
     silently change a preference that outlives the quote. */
  const declineAutoFullscreen = useCallback(() => {
    setFullscreenDeclined(true);
    try {
      window.localStorage.setItem(FULLSCREEN_OPT_OUT_KEY, "1");
    } catch {
      /* private mode / storage disabled — the session default still applies */
    }
  }, []);

  const requestClose = useCallback(
    ({ remember = false } = {}) => {
      if (remember) declineAutoFullscreen();
      if (hasUnsavedWork) {
        setUnsavedPrompt(true);
        return;
      }
      setShowModal(false);
    },
    [hasUnsavedWork, declineAutoFullscreen]
  );

  /* "Cramped by default": ~15 columns in a 530px workspace lane, with the
     Margin column clipped off the right edge. The host lane is what knows how
     little width it is handing over, so it asks (autoFullscreen) rather than
     this component guessing — the same table rendered standalone on the
     comparison page has the whole page and must not hijack it. Internal view
     only: the customer preview is a handful of columns and fits. */
  useEffect(() => {
    if (!autoFullscreen || loading || error) return;
    if (fullscreenDeclined || isCustomerMode) return;
    setShowModal(true);
    /* Deps are deliberately NOT exhaustive: fullscreenDeclined and
       isCustomerMode are read but omitted. Including them would re-run this the
       moment the operator closed the modal or switched to customer preview, and
       reopen it in their face. This fires on load, once. */
  }, [autoFullscreen, loading, error]);

  useEffect(() => {
    if (modalScrollRef.current && (scrollPositionRef.current.top > 0 || scrollPositionRef.current.left > 0)) {
      modalScrollRef.current.scrollTop = scrollPositionRef.current.top;
      modalScrollRef.current.scrollLeft = scrollPositionRef.current.left;
    }
  }, [allOtherQuoteOptions]);


  if (loading) {
    // Fullscreen is owned by this component's own modal (see the main return).
    // The skeleton used to forward the HOST's handler instead, so a Fullscreen
    // click while rates were still loading opened a second copy of the cost
    // table — a second fetch — on top of the inline one.
    return (
      <MaybeAccordion noAccordion={noAccordion} title="Final Quote Cost Table" defaultOpen={true} onFullscreen={() => setShowModal((s) => !s)} isFullscreen={showModal} showFullscreenButton={true} >
        <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)]">
          <TableSkeleton rows={8} columns={10} />
        </div>
      </MaybeAccordion>
    );
  }

  if (error) {
    return (
      <MaybeAccordion noAccordion={noAccordion} title="Final Quote Cost Table" defaultOpen={true}>
        <div className="p-6 text-center rounded-xl border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30">
          <FontAwesomeIcon
            icon={faTimes}
            className="text-[20px] text-red-500 mb-2"
          />
          <p className="text-sm text-red-500">
            Error: {error}
          </p>
        </div>
      </MaybeAccordion>
    );
  }


  /* P16 — snapshot what the customer is about to receive, in display order.
     The table is rebuilt from live rates on every load, so without this an
     inbound reply saying "option 2" resolves to nothing. Fire-and-forget: a
     failure here must never block the operator from sending. */
  const rememberSentOptions = (options) => {
    if (!quoteId || !Array.isArray(options) || options.length === 0) return;
    recordSentOptions({
      token: loginToken,
      role: userLoggedIn?.role,
      id: quoteId,
      options,
    }).catch((err) =>
      console.error("Could not record the options sent to the customer:", err)
    );
  };

  const triggerMyPanel = (e, myItem, conversation, task, dataForCalculation, multiple = false) => {
    e.preventDefault();
    setShowModal(false);
    if (typeof triggerPanel !== "function") return;
    triggerPanel(conversation || "customer", myItem, task || "quote_submission", dataForCalculation, multiple);
  };

  const getUniqueAirports = (type = "hauler") => {
    const airports = new Set();

    if (type === "hauler") {
      // Original logic for haulers - get destination airports from screeningPrices
      allHaulers.forEach(hauler => {
        if (hauler.screeningPrices && Array.isArray(hauler.screeningPrices)) {
          hauler.screeningPrices.forEach(price => {
            if (price.destinationAirport && price.destinationAirport !== "any") {
              airports.add(price.destinationAirport.toLowerCase().trim());
            }
          });
        }
      });
    } else if (type === "airline") {
      // New logic for airlines - get origin airports from originAirports field
      allAirlines.forEach(airline => {
        if (airline.originAirports && Array.isArray(airline.originAirports)) {
          airline.originAirports.forEach(airport => {
            if (airport && airport.trim() !== "") {
              airports.add(airport.toLowerCase().trim());
            }
          });
        }
      });
    }

    return Array.from(airports).sort();
  };


  const destinationAirports = getUniqueAirports("hauler");
  const originAirports = getUniqueAirports("airline");


  const savedCombinations = allHaulers.reduce((acc, hauler) => {
    let count = 0;
    allAirlines.forEach(airline => {
      if (Array.isArray(airline?.originAirports) && airline.originAirports.length > 0) {
        airline.originAirports.forEach(originAirport => {
          if (Array.isArray(hauler?.screeningPrices) && hauler.screeningPrices.length > 0) {
            hauler.screeningPrices.forEach(screeningPrice => {
              if (screeningPrice.destinationAirport.toLowerCase().trim() === originAirport.toLowerCase().trim()) {
                count++;
              }
            });
          }
        });
      }
    });
    return acc + count;
  }, 0);

  const totalCombinations = savedCombinations;

  return (
    <MaybeAccordion
      noAccordion={noAccordion}
      title="Final Quote Cost Table"
      defaultOpen={finalQuote.length > 0 || allOtherQuoteOptions.length > 0}
      onFullscreen={() => setShowModal((s) => !s)}
      isFullscreen={showModal}
      showFullscreenButton={true}
      naturalBodyHeight={true}
      headerAction={
        <HelpPopover
          label="Walkthrough"
          title="Final Quote Cost Table"
          sections={TABLE_WALKTHROUGH}
          align="right"
          size="lg"
        />
      }
    >
      <div className="min-h-0">
        {disabled && typeof disabled === 'string' && (
          <div className="mb-4 p-4 rounded-xl border border-red-200 bg-red-50 text-red-500 text-[13px] leading-relaxed dark:border-red-900 dark:bg-red-950/30">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-[14px] mt-0.5 shrink-0" />
            <span>{disabled}</span>
          </div>
        )}
        {finalQuote.length == 0 ? (
          <div className="">
            {/* Summary strip — small kicker + unified pill stats so operator sees the shape of the table immediately */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                  Preview
                </div>
                <h3 className="mt-0.5 truncate text-[15px] font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                  Available combinations
                </h3>
              </div>
              <div
                className="inline-flex items-center gap-0 rounded-full border border-slate-200/80 bg-white p-0.5 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.6)]"
                role="group"
                aria-label="Combinations summary"
              >
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px]">
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
                    <FontAwesomeIcon icon={faPlane} className="text-[8px]" />
                  </span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {allAirlines.length}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">Airlines</span>
                </span>
                <span className="h-3 w-px bg-slate-200 dark:bg-[oklch(0.3_0.024_264)]" aria-hidden />
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px]">
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                    <FontAwesomeIcon icon={faTruck} className="text-[8px]" />
                  </span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {allHaulers.length}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">Transport</span>
                </span>
                <span className="h-3 w-px bg-slate-200 dark:bg-[oklch(0.3_0.024_264)]" aria-hidden />
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px]">
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
                    <FontAwesomeIcon icon={faTable} className="text-[8px]" />
                  </span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {totalCombinations}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">
                    Combinations
                  </span>
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {/* Airlines card */}
              <motion.div
                className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_8px_24px_-16px_rgba(15,23,42,0.08)] dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)]"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-[linear-gradient(180deg,#f8fbff_0%,#ffffff_100%)] px-4 py-2.5 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)]">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
                      <FontAwesomeIcon icon={faPlane} className="text-[11px]" />
                    </span>
                    <h4 className="text-[12.5px] font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                      Airlines
                    </h4>
                    <span className="inline-flex h-5 items-center rounded-full bg-slate-100 px-2 text-[10px] font-semibold text-slate-600 dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-300">
                      {allAirlines.length}
                    </span>
                  </div>
                  <span className="text-[10.5px] text-slate-400 dark:text-slate-500">
                    Rate per kg · transit · origins
                  </span>
                </div>
                <div className="overflow-x-auto max-h-full">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50/80 text-[10.5px] uppercase tracking-[0.08em] dark:bg-[oklch(0.225_0.026_264/0.6)]">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-slate-400">Airline</th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-500 dark:text-slate-400">Rate</th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-500 dark:text-slate-400">Min Charge</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-500 dark:text-slate-400">Transit</th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-slate-400">Origins</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-[oklch(0.3_0.02_264/0.6)]">
                      {allAirlines.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-3 py-8 text-center text-[12px] text-slate-400 dark:text-slate-500">
                            No airlines saved yet.
                          </td>
                        </tr>
                      ) : allAirlines.map((airline) => (
                        <tr
                          key={airline._id}
                          className="transition-colors hover:bg-slate-50/60 dark:hover:bg-[oklch(0.26_0.028_264/0.4)]"
                        >
                          <td className="px-3 py-2.5">
                            <div className="text-[13px] font-medium text-slate-800 dark:text-slate-100">
                              {airline.airlineDetail?.name}
                            </div>
                            <div className="mt-0.5 font-mono text-[10.5px] text-slate-400 dark:text-slate-500">
                              {airline.airlineDetail?.airlineCode}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                            £{airline.finalRate?.toFixed(2)}
                            <span className="ml-0.5 text-[10px] font-normal text-slate-400">/kg</span>
                          </td>
                          <td className="px-3 py-2.5 text-right text-[12px] text-slate-500 dark:text-slate-400">
                            {airline.minCharge ? `£${airline.minCharge}` : "—"}
                          </td>
                          <td className="px-3 py-2.5 text-center text-[11.5px] text-slate-500 dark:text-slate-400">
                            {airline.transitTime || "—"}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex flex-wrap gap-1">
                              {Array.isArray(airline?.originAirports) && airline.originAirports.length > 0 ? (
                                airline.originAirports.map((airport, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 font-mono text-[10.5px] font-semibold uppercase tracking-wider text-blue-700 dark:bg-blue-500/15 dark:text-blue-300"
                                  >
                                    {airport}
                                  </span>
                                ))
                              ) : typeof triggerPanel === "function" ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    triggerPanel("airline", undefined, undefined, undefined, undefined, "selectedAirlineId", airline.airlineId, { 0: false });
                                  }}
                                  className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-0.5 text-[10.5px] font-medium text-slate-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 dark:border-[oklch(0.33_0.022_264)] dark:text-slate-400 dark:hover:border-blue-500/30 dark:hover:bg-blue-500/10"
                                >
                                  + Add origin
                                </button>
                              ) : (
                                <span className="text-[11px] text-slate-400 dark:text-slate-500">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* Transport agents card */}
              <motion.div
                className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_8px_24px_-16px_rgba(15,23,42,0.08)] dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)]"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-[linear-gradient(180deg,#f3fbf6_0%,#ffffff_100%)] px-4 py-2.5 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)]">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                      <FontAwesomeIcon icon={faTruck} className="text-[11px]" />
                    </span>
                    <h4 className="text-[12.5px] font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                      Transport agents
                    </h4>
                    <span className="inline-flex h-5 items-center rounded-full bg-slate-100 px-2 text-[10px] font-semibold text-slate-600 dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-300">
                      {allHaulers.length}
                    </span>
                  </div>
                  <span className="text-[10.5px] text-slate-400 dark:text-slate-500">
                    Basic rate · type · destinations
                  </span>
                </div>
                <div className="overflow-x-auto max-h-full">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50/80 text-[10.5px] uppercase tracking-[0.08em] dark:bg-[oklch(0.225_0.026_264/0.6)]">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-slate-400">Agent</th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-500 dark:text-slate-400">Basic Rate</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-500 dark:text-slate-400">Type</th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-slate-400">Destinations</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-[oklch(0.3_0.02_264/0.6)]">
                      {allHaulers.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-3 py-8 text-center text-[12px] text-slate-400 dark:text-slate-500">
                            No transport agents saved yet — add one in the Transport lane to enable combinations.
                          </td>
                        </tr>
                      ) : allHaulers.map((hauler) => (
                        <tr
                          key={hauler._id}
                          className="transition-colors hover:bg-slate-50/60 dark:hover:bg-[oklch(0.26_0.028_264/0.4)]"
                        >
                          <td className="px-3 py-2.5">
                            <div className="text-[13px] font-medium text-slate-800 dark:text-slate-100">
                              {hauler.agentDetail?.name}
                            </div>
                            <div className="mt-0.5 font-mono text-[10.5px] text-slate-400 dark:text-slate-500">
                              {hauler.agentDetail?.agentCode}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                            £{hauler.finalRate?.toFixed(2)}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {hauler.agentDetail?.agentType ? (
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${hauler.agentDetail?.agentType === 'transport'
                                ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                                : 'bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300'
                                }`}>
                                {hauler.agentDetail?.agentType}
                              </span>
                            ) : (
                              <span className="text-[11px] text-slate-400 dark:text-slate-500">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex flex-wrap gap-1">
                              {Array.isArray(hauler?.screeningPrices) &&
                                hauler.screeningPrices.some(
                                  p => typeof p.destinationAirport === "string" && p.destinationAirport.trim() !== ""
                                ) ? (
                                hauler.screeningPrices
                                  .filter(
                                    p => typeof p.destinationAirport === "string" && p.destinationAirport.trim() !== ""
                                  )
                                  .map((price, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 font-mono text-[10.5px] font-semibold uppercase tracking-wider text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                                    >
                                      {price.destinationAirport}
                                    </span>
                                  ))
                              ) : typeof triggerPanel === "function" ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    triggerPanel("transport", undefined, undefined, undefined, undefined, "selectedTransportId", hauler.agentId, { 0: false });
                                  }}
                                  className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-0.5 text-[10.5px] font-medium text-slate-500 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-600 dark:border-[oklch(0.33_0.022_264)] dark:text-slate-400 dark:hover:border-emerald-500/30 dark:hover:bg-emerald-500/10"
                                >
                                  + Add destination
                                </button>
                              ) : (
                                <span className="text-[11px] text-slate-400 dark:text-slate-500">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </div>

          </div>) : (
          <>
            <div id={customerExportInlineId}>
              <QuoteCostTableBlock
                tableRefs={tableRefs}
                isDarkMode={isDarkMode}
                selectedScreening={selectedScreening}
                key={1}
                quoteOptions={finalQuote}
                isAllOptions={true}
                isCustomerMode={isCustomerMode}
                saveThisTable={saveThisTable}
                indeX={1}
                agentC={finalQuote[0]?.agentCode}
                handleChange={handleChange}
                onScreeningSelectChange={onScreeningSelectChange}
                title={
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                      <FontAwesomeIcon icon={faTruck} className="text-[11px]" />
                    </span>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                      {finalQuote[0]?.agentType || "Transport"}
                    </span>
                    <span className="font-mono text-[12.5px] font-semibold text-slate-900 dark:text-slate-100">
                      {finalQuote[0]?.agentCode}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                      <FontAwesomeIcon icon={faCircleCheck} className="text-[9px]" />
                      Finalised
                    </span>
                  </div>
                }
                refs={
                  finalQuote[0]?.agentCode +
                  "_" +
                  finalQuote[0]?.screeningPrices
                    ?.map((x) => x?.destinationAirport)
                    .join("-")
                }
                isFinalizedQuote={true}
              />
            </div>
          </>
        )}
        <div className="mt-5 space-y-3">
          {!isCustomerMode ? (
            <>
              {/* Airports + Save button — unified row */}
              {finalQuote.length == 0 && (() => {
                const disabledCta = totalCombinations === 0 || (finalQuote.length === 0 && allOtherQuoteOptions.length === 0);
                const canGenerate = !!generateOrNot.length;
                const hasFinalRate = Number(generateOrNot[0]?.dataForCalculation?.finalRate) > 0
                  && !!generateOrNot[0]?.dataForCalculation?.SelectedScreening;
                const canFinalize = canGenerate && generateOrNot.length === 1 && isVantRefIdValid && hasFinalRate;
                return (
                  <>
                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
                      {/* Airports strip — content-sized on desktop (no flex-1 stretch) */}
                      {destinationAirports.length > 0 && (
                        <div className="w-full max-w-full rounded-xl border border-slate-200/90 bg-white px-3 py-2 shadow-[0_1px_0_rgba(255,255,255,0.85)_inset] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.5)] sm:w-auto sm:max-w-[min(100%,32rem)]">
                          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1.5">
                            <span className="inline-flex shrink-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                              <FontAwesomeIcon icon={faLocationDot} className="text-[10px] text-fk-accent/80" />
                              Distinct airports for air-freighting
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                              {destinationAirports.map((airport) => (
                                <span
                                  key={airport}
                                  className="rounded-md border border-slate-200/80 bg-slate-50/90 px-2 py-0.5 font-mono text-[10.5px] font-semibold uppercase tracking-wider text-slate-700 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264/0.8)] dark:text-slate-200"
                                >
                                  {airport}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      <>
                        <motion.button className={`inline-flex my-2 shrink-0 items-center justify-center gap-2 self-start rounded-xl px-5 py-2.5 text-[12.5px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 sm:self-center sm:min-w-[11.5rem] border border-fk-accent bg-fk-accent text-white shadow-[0_6px_18px_-6px_rgba(37,99,235,0.35)] hover:brightness-110 hover:shadow-[0_8px_22px_-6px_rgba(37,99,235,0.42)] active:brightness-95 dark:border-sky-400/40 dark:bg-fk-accent dark:text-white`} onClick={() => setShowModal((s) => !s)}>Open in full view to see tables</motion.button>
                      </>
                    </div>
                  </>
                );
              })()}
            </>
          ) : (
            <div className="flex flex-wrap items-center justify-end gap-2 rounded-xl border border-slate-200/80 bg-slate-50/60 px-2.5 py-2 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.4)]">
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  onClick={handleBackToInternalTable}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-[12px] font-semibold text-amber-700 transition-colors hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-[11px]" />
                  Back to Internal View
                </button>
                {/* <button
                  onClick={handlePrint}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600 transition-colors hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
                >
                  <FontAwesomeIcon icon={faPrint} className="text-[11px]" />
                  Print
                </button>
                <button
                  onClick={handleDownloadCsv}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600 transition-colors hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
                >
                  <FontAwesomeIcon icon={faFileCsv} className="text-[11px]" />
                  Download CSV
                </button>
                <button
                  type="button"
                  onClick={handleCopyForGmail}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-[12px] font-semibold text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-500/30 dark:bg-teal-500/10 dark:text-teal-300"
                >
                  <FontAwesomeIcon icon={faEnvelope} className="text-[11px]" />
                  Copy for Gmail
                </button> */}
                {quoteSource === "manual" ? (
                  <button
                    type="button"
                    onClick={handleCopyCustomerView}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200 dark:hover:bg-[oklch(0.26_0.028_264)]"
                  >
                    <FontAwesomeIcon icon={faCopy} className="text-[11px]" />
                    Copy customer view
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
      {showModal && createPortal(
        <AnimatePresence>
          <motion.div
            data-testid="cost-table-fullscreen"
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            // Backdrop: closes, but does not record a preference — a misclick
            // on the dimmed area is not a decision about every future quote.
            onClick={() => requestClose()}
          >
            <motion.div
              ref={modalScrollRef}
              onScroll={handleModalScroll}
              className="fixed inset-0 w-screen h-screen overflow-auto bg-[linear-gradient(180deg,#f8fafc_0%,#ffffff_100%)] shadow-xl dark:bg-[linear-gradient(180deg,#0B1120_0%,#0F1419_100%)]"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal toolbar — sticky header with breadcrumb, walkthrough and close */}
              <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-slate-200/80 bg-white/85 px-4 py-2.5 backdrop-blur dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)]/85 sm:px-6">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white dark:bg-white/10">
                    <FontAwesomeIcon icon={faTable} className="text-[12px]" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                      Final Quote Cost Table
                    </div>
                    <div className="truncate text-[13px] font-semibold text-slate-900 dark:text-slate-100">
                      {finalQuote.length > 0 ? "Finalised option" : (isCustomerMode ? "Customer preview" : "Full combination table")}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {!isCustomerMode && (
                    <DraftStatusChip
                      status={draft.status}
                      lastSavedAt={draft.lastSavedAt}
                      error={draft.error}
                    />
                  )}
                  {/* Single source of truth for internal/customer view. Replaces
                      the legacy "Generate table" / "Back to Internal View"
                      buttons that lived in two different footer states and
                      duplicated each other (both just toggled isCustomerMode). */}
                  {(() => {
                    const noneSelected = !Array.isArray(generateOrNot) || generateOrNot.length === 0;
                    const anyZeroTotal = Array.isArray(generateOrNot) && generateOrNot.some(
                      (opt) => !(Number(opt.dataForCalculation?.GrandTotal) > 0) || !opt.dataForCalculation?.SelectedScreening
                    );
                    const canPreview = !noneSelected && !anyZeroTotal;
                    const previewDisabled = !isCustomerMode && !canPreview;
                    const previewTitle = isCustomerMode
                      ? "Currently showing the customer view (internal columns hidden). Click to return to the internal view."
                      : noneSelected
                        ? "Select at least one row to preview the customer view."
                        : anyZeroTotal
                          ? "Some selected rows have no total — select a screening type and ensure rates are entered."
                          : "Preview what the customer sees: hides Margin %, Customs £, Extra £ and the screening breakdown.";
                    return (
                      <motion.button
                        type="button"
                        onClick={() => { if (!previewDisabled) setIsCustomerMode((v) => !v); }}
                        whileHover={previewDisabled ? undefined : { y: -1 }}
                        whileTap={previewDisabled ? undefined : { scale: 0.96 }}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${previewDisabled
                          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-500 opacity-60"
                          : isCustomerMode
                            ? "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-300"
                            : "border-slate-200 bg-white text-slate-600 hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-300 dark:hover:border-violet-500/30 dark:hover:bg-violet-500/10 dark:hover:text-violet-300"
                          }`}
                        title={previewTitle}
                      >
                        <FontAwesomeIcon
                          icon={isCustomerMode ? faEyeSlash : faEye}
                          className="text-[11px]"
                        />
                        {isCustomerMode ? "Customer view" : "Preview as customer"}
                      </motion.button>
                    );
                  })()}
                  <HelpPopover
                    label="Walkthrough"
                    title="Final Quote Cost Table"
                    sections={TABLE_WALKTHROUGH}
                    align="right"
                    size="lg"
                  />
                  <motion.button
                    // Explicit Close: remembered, so we stop opening it.
                    onClick={() => requestClose({ remember: true })}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600 hover:border-red-200 hover:bg-red-50 hover:text-red-600 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-300 dark:hover:border-red-500/30 dark:hover:bg-red-500/10 dark:hover:text-red-300"
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.96 }}
                    aria-label="Close"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-[11px]" />
                    Close
                  </motion.button>
                </div>
              </div>
              <div className="p-4 sm:p-6" ref={printAreaRef}>
                {finalQuote.length > 0 ? (
                  <>
                    <div id={customerExportModalId}>
                      <QuoteCostTableBlock
                        tableRefs={tableRefs}
                        isDarkMode={isDarkMode}
                        selectedScreening={selectedScreening}
                        key={1}
                        quoteOptions={finalQuote}
                        isAllOptions={true}
                        isCustomerMode={isCustomerMode}
                        saveThisTable={saveThisTable}
                        indeX={1}
                        agentC={finalQuote[0]?.agentCode}
                        handleChange={handleChange}
                        onScreeningSelectChange={onScreeningSelectChange}
                        title={
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                              <FontAwesomeIcon icon={faTruck} className="text-[11px]" />
                            </span>
                            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                              {finalQuote[0]?.agentType || "Transport"}
                            </span>
                            <span className="font-mono text-[12.5px] font-semibold text-slate-900 dark:text-slate-100">
                              {finalQuote[0]?.agentCode}
                            </span>
                            <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                              <FontAwesomeIcon icon={faCircleCheck} className="text-[9px]" />
                              Finalised
                            </span>
                          </div>
                        }
                        refs={
                          finalQuote[0]?.agentCode +
                          "_" +
                          finalQuote[0]?.screeningPrices
                            ?.map((x) => x?.destinationAirport)
                            .join("-")
                        }
                        isFinalizedQuote={true}
                      />
                    </div>
                    {/* Hidden customer-mode shadow of the finalised table. The
                      "Send to Customer Conversation" button captures THIS
                      DOM (via tableRefs.current[code + "__customer"]) so
                      internal-only columns (Margin %, Customs £, Extra £,
                      Freight £/kg, War Risk £/kg, Handling £/kg, Fuel %,
                      Screening Transport/Airline, Transport £) never leak
                      into the customer email. The visible block above
                      still honours `isCustomerMode` so operators keep
                      their full breakdown internally. */}
                    <div
                      aria-hidden="true"
                      style={{
                        position: "fixed",
                        left: -99999,
                        top: -99999,
                        width: 1200,
                        pointerEvents: "none",
                        opacity: 0,
                      }}
                    >
                      <QuoteCostTableBlock
                        tableRefs={tableRefs}
                        isDarkMode={isDarkMode}
                        selectedScreening={selectedScreening}
                        key="finalised-customer-shadow"
                        quoteOptions={finalQuote}
                        isAllOptions={true}
                        isCustomerMode={true}
                        saveThisTable={saveThisTable}
                        indeX={1}
                        agentC={finalQuote[0]?.agentCode}
                        handleChange={handleChange}
                        onScreeningSelectChange={onScreeningSelectChange}
                        title={
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                              <FontAwesomeIcon icon={faTruck} className="text-[11px]" />
                            </span>
                            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                              {finalQuote[0]?.agentType || "Transport"}
                            </span>
                            <span className="font-mono text-[12.5px] font-semibold text-slate-900">
                              {finalQuote[0]?.agentCode}
                            </span>
                            <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                              <FontAwesomeIcon icon={faCircleCheck} className="text-[9px]" />
                              Finalised
                            </span>
                          </div>
                        }
                        refs={
                          (finalQuote[0]?.agentCode || "") +
                          "_" +
                          (finalQuote[0]?.screeningPrices
                            ?.map((x) => x?.destinationAirport)
                            .join("-") || "") +
                          "__customer"
                        }
                        isFinalizedQuote={true}
                      />
                    </div>
                  </>
                ) : <>
                  {/* {isCustomerMode && saveThisTable && typeof triggerPanel === "function" && (
                    <div className="flex my-5 items-center gap-x-4">
                      <div
                        className={"px-2 py-1 text-center items-center flex text-[16px] cursor-pointer bg-[#009688] text-white rounded-md hover:bg-[#00796B]"}
                        onClick={(e) => {
                          e.stopPropagation();
                          const selectedOptions = allOtherQuoteOptions.flat().filter((option) =>
                            option.screeningPrices.map((x) => x.selectBox).includes(true)
                          );
                          const codes = [];
                          selectedOptions.forEach((X) => {
                            codes.push(X?.agentCode + "_" + X?.screeningPrices?.map((x) => x?.destinationAirport).join("-"));
                          });
                          triggerMyPanel(e, codes, undefined, undefined, undefined, true);
                        }}
                      >
                        Send to Customer Conversation1
                      </div>
                    </div>
                  )} */}
                  {/* Without this the full-view modal rendered a header, a
                      footer and nothing in between whenever no transport-agent
                      rate had been saved yet (airline in, haulier still to
                      reply) — indistinguishable from a broken screen. */}
                  {allOtherQuoteOptions.length === 0 && (
                    <div className="rounded-xl border border-amber-300/50 bg-amber-50 px-4 py-3 text-[12.5px] text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200">
                      <p className="font-semibold">No priced combinations yet</p>
                      <p className="mt-1 opacity-90">
                        A cost table needs both an airline rate and a transport
                        agent rate on this quote. Save the missing side and this
                        table will build itself.
                      </p>
                    </div>
                  )}
                  {allOtherQuoteOptions.length > 0 && (
                    <div id={customerExportModalId}>
                      {allOtherQuoteOptions
                        .filter(group =>
                          !isCustomerMode || group.some(opt => opt.screeningPrices.some(sp => sp.selectBox))
                        )
                        .map((allOtherQuoteOption, indeX) => {
                          return (
                            <QuoteCostTableBlock
                              tableRefs={tableRefs}
                              isDarkMode={isDarkMode}
                              selectedScreening={selectedScreening}
                              key={indeX}
                              quoteOptions={allOtherQuoteOption}
                              isAllOptions={true}
                              isCustomerMode={isCustomerMode}
                              saveThisTable={saveThisTable}
                              indeX={indeX}
                              agentC={allOtherQuoteOption[0]?.agentCode}
                              handleChange={handleChange}
                              onScreeningSelectChange={onScreeningSelectChange}
                              title={
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-blue-50 text-fk-accent dark:bg-blue-500/15 dark:text-blue-300">
                                    <FontAwesomeIcon icon={faTruck} className="text-[11px]" />
                                  </span>
                                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                                    {allOtherQuoteOption[0]?.agentType || "Transport"}
                                  </span>
                                  <span className="font-mono text-[12.5px] font-semibold text-slate-900 dark:text-slate-100">
                                    {allOtherQuoteOption[0]?.agentCode}
                                  </span>
                                  <span className="ml-auto text-[10.5px] text-slate-400 dark:text-slate-500">
                                    {allOtherQuoteOption.length} airline{allOtherQuoteOption.length === 1 ? "" : "s"}
                                  </span>
                                </div>
                              }
                              refs={
                                allOtherQuoteOption[0]?.agentCode +
                                "_" +
                                allOtherQuoteOption[0]?.screeningPrices
                                  ?.map((x) => x?.destinationAirport)
                                  .join("-")
                              }
                            />
                          );
                        })}
                    </div>
                  )}

                  {/* Hidden customer-mode twins of the ticked groups. The
                      "Send options to customer" action captures these, so the
                      operator keeps their full breakdown on screen while the
                      customer receives only what they should see. Mirrors the
                      finalised-table shadow above. */}
                  <div
                    aria-hidden="true"
                    style={{ position: "fixed", left: -99999, top: -99999, width: 1200, pointerEvents: "none", opacity: 0 }}
                  >
                    {allOtherQuoteOptions
                      .filter(group => group.some(opt => opt.screeningPrices.some(sp => sp.selectBox)))
                      .map((group, i) => (
                        <QuoteCostTableBlock
                          tableRefs={tableRefs}
                          isDarkMode={isDarkMode}
                          selectedScreening={selectedScreening}
                          key={`customer-shadow-${i}`}
                          quoteOptions={group}
                          isAllOptions={true}
                          isCustomerMode={true}
                          saveThisTable={saveThisTable}
                          indeX={i}
                          agentC={group[0]?.agentCode}
                          handleChange={handleChange}
                          onScreeningSelectChange={onScreeningSelectChange}
                          title={
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                {group[0]?.agentType || "Transport"}
                              </span>
                              <span className="font-mono text-[12.5px] font-semibold text-slate-900">
                                {group[0]?.agentCode}
                              </span>
                            </div>
                          }
                          refs={
                            (group[0]?.agentCode || "") + "_" +
                            (group[0]?.screeningPrices?.map((x) => x?.destinationAirport).join("-") || "") +
                            "__customer"
                          }
                        />
                      ))}
                  </div>
                </>}
              </div>
              <div className="border-t border-slate-200/80 bg-slate-50/40 px-4 pb-5 pt-4 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.3)] sm:px-6">
                <div className="flex flex-col gap-4">
                  {finalQuote?.[0]?.vantRef ?
                    <div className="flex flex-wrap items-center gap-3">
                      {typeof triggerPanel === "function" && finalQuote[0]?.agentCode && (
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Use the "__customer" shadow ref so the captured
                            // DOM is the isCustomerMode=true rendering — strips
                            // margin/customs/extra/freight breakdown before
                            // the table HTML is dropped into the customer email.
                            const code =
                              (finalQuote[0]?.agentCode || "") +
                              "_" +
                              (finalQuote[0]?.screeningPrices?.map((x) => x?.destinationAirport).join("-") || "") +
                              "__customer";
                            // Force the "NS: Nomination Confirmation - Customer"
                            // template (purpose = "nomination_confirmation",
                            // per backend emailTemplateModel.js:56). The
                            // generic "quote_submission" was the wrong fit
                            // post-finalise — at this point the option is
                            // chosen, not pitched.
                            rememberSentOptions(finalQuote);
                            triggerMyPanel(
                              e,
                              [code],
                              undefined,
                              "nomination_confirmation",
                              undefined,
                              true
                            );
                          }}
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.96 }}
                          className="inline-flex items-center gap-2 rounded-xl border border-emerald-600/10 bg-[linear-gradient(180deg,#059669_0%,#047857_100%)] px-4 py-2.5 text-[12.5px] font-semibold text-white shadow-[0_8px_22px_-10px_rgba(5,150,105,0.5)] hover:brightness-110 active:brightness-95 dark:border-emerald-400/30"
                          title="Attach this finalised table to the Customer Conversation reply composer. Safe to use again if you need to re-send."
                        >
                          <FontAwesomeIcon icon={faPaperPlane} className="text-[12px]" />
                          Send to Customer Conversation
                        </motion.button>
                      )}
                      {/* Copy-for-email — primary action when there's no
                          Customer Conversation panel (manual-query flow),
                          secondary "plain copy" when there is. Always uses
                          the hidden customer-mode shadow so the clipboard
                          payload is already stripped of internal columns. */}
                      <motion.button
                        type="button"
                        onClick={handleCopyFinalisedForEmail}
                        whileHover={{ y: -1 }}
                        whileTap={{ scale: 0.96 }}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[12.5px] font-semibold text-slate-700 shadow-sm transition hover:border-fk-accent hover:text-fk-accent dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200 dark:hover:border-fk-accent"
                        title="Copy the finalised customer table (rich HTML) to paste into Gmail / your email client"
                      >
                        <FontAwesomeIcon icon={faCopy} className="text-[12px]" />
                        Copy for email
                      </motion.button>
                      <div className="ml-auto inline-flex items-center gap-3 rounded-2xl border border-blue-200 bg-[linear-gradient(180deg,#eff6ff_0%,#ffffff_100%)] px-4 py-3 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_8px_20px_-12px_rgba(37,99,235,0.25)] dark:border-blue-900 dark:bg-blue-950/30">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300">
                          <FontAwesomeIcon icon={faIdCard} className="text-[13px]" />
                        </span>
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-600/80 dark:text-blue-300/80">
                            VANT Ref
                          </div>
                          <div className="font-mono text-[15px] font-semibold text-slate-900 dark:text-slate-100">
                            {finalQuote[0].vantRef}
                          </div>
                        </div>
                      </div>
                    </div>
                    : (!isCustomerMode ? (
                      <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-5 lg:items-start lg:gap-5">
                        <div className="flex flex-col gap-4 lg:col-span-3">
                          {/* Adjust Common panel */}
                          <section className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_0_rgba(255,255,255,0.9)_inset] dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)]">
                            <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5 dark:border-[oklch(0.3_0.02_264)]">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-fk-accent/10 text-fk-accent">
                                  <FontAwesomeIcon icon={faSlidersH} className="text-[11px]" />
                                </span>
                                <h4 className="text-[13px] font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                                  Adjust common
                                </h4>
                                <span className="text-[10.5px] text-slate-400 dark:text-slate-500">
                                  Bulk-apply to every row
                                </span>
                              </div>
                              <HelpPopover
                                label="What is this?"
                                title="Adjust common"
                                sections={ADJUST_COMMON_WALKTHROUGH}
                                align="right"
                              />
                            </div>
                            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
                              {[
                                { key: "profitMargin", label: "Profit margin", suffix: "%" },
                                { key: "custom", label: "Custom rate", suffix: "£" },
                                { key: "extra", label: "Extra charges", suffix: "£" },
                              ].map((f) => {
                                const sp0 = allOtherQuoteOptions?.[0]?.[0]?.screeningPrices?.[0];
                                const value = sp0 ? (sp0[f.key] || 0) : 0;
                                const disabled = (finalQuote && finalQuote.length > 0 && finalQuote[0]?.selectedScreening) || saveThisTable;
                                return (
                                  <div key={f.key} className="flex flex-col gap-1">
                                    <label className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                                      {f.label}
                                    </label>
                                    <div className={`flex items-stretch rounded-lg border transition-colors focus-within:border-fk-accent focus-within:ring-2 focus-within:ring-fk-accent/10 ${disabled ? "border-slate-200 bg-slate-100/60 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264/0.6)]" : "border-slate-200 bg-white dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)]"}`}>
                                      <input
                                        type="number"
                                        value={value}
                                        onChange={(e) => handleChange(null, f.key, e.target.value, true, "all")}
                                        onWheel={blurOnWheel}
                                        className="w-full bg-transparent px-2.5 py-1.5 text-[12.5px] font-semibold text-slate-800 outline-none dark:text-slate-200"
                                        disabled={disabled}
                                      />
                                      <span className="inline-flex items-center border-l border-slate-200 px-2 text-[11px] font-semibold text-slate-400 dark:border-[oklch(0.33_0.022_264)] dark:text-slate-500">
                                        {f.suffix}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </section>

                          {/* VANT Ref Id panel */}
                          <section className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_0_rgba(255,255,255,0.9)_inset] dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)]">
                            <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5 dark:border-[oklch(0.3_0.02_264)]">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
                                  <FontAwesomeIcon icon={faIdCard} className="text-[11px]" />
                                </span>
                                <h4 className="text-[13px] font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                                  VANT Ref Id
                                </h4>
                                <span
                                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${isVantRefIdValid && vantRefId.sequenceNumber > lastInsertedValue
                                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                                    : "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                                    }`}
                                >
                                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${isVantRefIdValid && vantRefId.sequenceNumber > lastInsertedValue ? "bg-emerald-500" : "bg-amber-500"}`} aria-hidden />
                                  {isVantRefIdValid && vantRefId.sequenceNumber > lastInsertedValue ? "Valid" : "Incomplete"}
                                </span>
                              </div>
                              <HelpPopover
                                label="What is this?"
                                title="VANT Ref Id"
                                sections={VANT_WALKTHROUGH}
                                align="right"
                              />
                            </div>
                            <div className="p-4">
                              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200/70 bg-slate-50/60 px-3 py-2 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.4)]">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                                    Generated ID
                                  </span>
                                  <span className="rounded-md border border-slate-200 bg-white px-2 py-0.5 font-mono text-[12.5px] font-semibold text-slate-900 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100">
                                    {generateVantRefIdString()}
                                  </span>
                                </div>
                                <Tooltip content={vantCopied ? "Copied!" : "Copy VANT Ref to clipboard"} position="top">
                                  <motion.button
                                    type="button"
                                    onClick={handleCopyVantRef}
                                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:border-fk-accent hover:text-fk-accent dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-300"
                                    whileHover={{ y: -1 }}
                                    whileTap={{ scale: 0.96 }}
                                  >
                                    <FontAwesomeIcon icon={vantCopied ? faCheck : faCopy} className="text-[11px]" />
                                    {vantCopied ? "Copied" : "Copy"}
                                  </motion.button>
                                </Tooltip>
                              </div>
                              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                                {[
                                  { label: "Company", value: vantRefId.companyName, disabled: true },
                                  { label: "User", value: vantRefId?.userName, disabled: true },
                                  { label: "Year", value: vantRefId.Year, disabled: true, placeholder: "YY" },
                                  { label: "Month", value: vantRefId.Month, disabled: true, placeholder: "MM" },
                                ].map((f) => (
                                  <div key={f.label} className="flex flex-col gap-1">
                                    <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400 dark:text-slate-500">
                                      {f.label}
                                    </label>
                                    <input
                                      type="text"
                                      value={f.value}
                                      disabled={f.disabled}
                                      placeholder={f.placeholder}
                                      className="w-full rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-center font-mono text-[12px] font-semibold text-slate-600 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-300"
                                    />
                                  </div>
                                ))}
                                <div className="flex flex-col gap-1">
                                  <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-fk-accent">
                                    Seq (editable)
                                  </label>
                                  <input
                                    type="number"
                                    value={vantRefId.sequenceNumber}
                                    onChange={(e) => handleVantRefIdChange('sequenceNumber', parseInt(e.target.value) || 1)}
                                    onWheel={blurOnWheel}
                                    className={`w-full rounded-md border px-2 py-1 text-center font-mono text-[12px] font-semibold outline-none focus:ring-2 focus:ring-fk-accent/20 dark:text-slate-200 ${vantRefId.sequenceNumber <= lastInsertedValue
                                      ? "border-red-300 bg-red-50/40 text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300"
                                      : "border-fk-accent/40 bg-white text-slate-900 dark:border-fk-accent/30 dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100"
                                      }`}
                                    min={lastInsertedValue + 1}
                                  />
                                </div>
                              </div>
                              <div className="mt-2 flex items-center gap-1.5 text-[10.5px] text-slate-500 dark:text-slate-400">
                                <FontAwesomeIcon icon={faCircleInfo} className="text-[10px] text-slate-400" />
                                Sequence must be greater than the last used
                                <span className="font-mono font-semibold text-slate-700 dark:text-slate-300">
                                  ({lastInsertedValue})
                                </span>
                                .
                              </div>
                            </div>
                          </section>
                        </div>

                        {/* Right rail — workflow actions (height follows content; does not stretch to left column) */}
                        <aside
                          className="flex w-full flex-col gap-2.5 rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/90 p-3 shadow-sm ring-1 ring-slate-900/[0.03] dark:border-[oklch(0.33_0.022_264)] dark:from-[#0F1419] dark:to-slate-900/90 dark:ring-white/[0.04] lg:col-span-2 lg:self-start"
                          aria-label="Finalise workflow actions"
                        >
                          <div className="flex items-start gap-2 border-b border-slate-200/70 pb-2.5 dark:border-[oklch(0.33_0.022_264/0.8)]">
                            <span
                              className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-fk-accent/12 text-fk-accent ring-1 ring-fk-accent/15 dark:bg-fk-accent/20 dark:text-blue-200"
                              aria-hidden
                            >
                              <FontAwesomeIcon icon={faCalculator} className="text-[12px]" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <h4 className="text-[13px] font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                                Quote workflow
                              </h4>
                              <p className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                                Send the customer the options to choose from, then finalise the one they pick.
                              </p>
                            </div>
                          </div>
                          {(() => {
                            /* One place that answers "why can't I finalise yet?".
                               These conditions used to live only in a `title`
                               tooltip on the disabled button, so the blockers
                               were invisible unless you hovered a dead control. */
                            const picked = generateOrNot.length;
                            const row = generateOrNot[0];
                            const steps = [
                              {
                                done: picked > 0 && !!row?.dataForCalculation?.SelectedScreening,
                                label: "Pick a screening type on a row",
                                hint: "PTS, STS, PAS or SAS — this is what prices the row.",
                              },
                              {
                                done: picked === 1,
                                label: "Tick exactly one row to nominate",
                                hint:
                                  picked > 1
                                    ? `${picked} rows are ticked — fine for sending options, but a nomination is a single one.`
                                    : "The tick box enables once the row has a total.",
                              },
                              {
                                done: Number(row?.dataForCalculation?.finalRate) > 0,
                                label: "Row totals more than £0",
                                hint: "Check the rates and screening on that row.",
                              },
                              {
                                done: isVantRefIdValid,
                                label: "Valid VANT Ref Id",
                                hint: "Sequence must be higher than the last used.",
                              },
                            ];
                            const blockers = steps.filter((x) => !x.done);
                            const ready = blockers.length === 0;

                            /* Step 1 of the real flow: pitch SEVERAL priced
                               options and let the customer pick. This lived
                               only behind "Preview as customer", which
                               self-disables whenever any ticked row is unpriced
                               — so the operator had no way to send a shortlist
                               at all. Nominating one option is a different job
                               and keeps its own button below. */
                            const priced = generateOrNot.filter(
                              (o) => Number(o?.dataForCalculation?.GrandTotal) > 0 &&
                                !!o?.dataForCalculation?.SelectedScreening
                            );
                            const canSend = priced.length > 0 && priced.length === picked;
                            const sendLabel =
                              picked === 0
                                ? "Tick the options to send"
                                : `Send ${priced.length} option${priced.length === 1 ? "" : "s"} to customer`;

                            const sendToCustomer = (e) => {
                              const codes = priced.map(
                                (o) =>
                                  (o?.agentCode || "") + "_" +
                                  (o?.screeningPrices?.map((x) => x?.destinationAirport).join("-") || "") +
                                  "__customer"
                              );
                              rememberSentOptions(priced);
                              triggerMyPanel(e, codes, undefined, undefined, undefined, true);
                            };

                            const saveLook = {
                              saving: { label: "Saving…", icon: faCircleNotch, spin: true },
                              error: { label: "Retry save", icon: faTriangleExclamation },
                              dirty: { label: "Save pricing", icon: faSave },
                              saved: { label: "Saved", icon: faCircleCheck },
                              clean: { label: "Saved", icon: faCircleCheck },
                            }[draft.status] || { label: "Save pricing", icon: faSave };
                            const saveIsIdle = draft.status === "clean" || draft.status === "saved";

                            return (
                              <div className="flex flex-col gap-2">
                                {/* Step 0: get the operator's own pricing into the
                                    database, and say so. Autosave still runs
                                    underneath; this is the one that answers "did
                                    it work?" without closing the table to find out. */}
                                <motion.button
                                  onClick={savePricing}
                                  disabled={draft.status === "saving" || !draft.canPersist}
                                  whileHover={draft.status === "saving" ? {} : { y: -1 }}
                                  whileTap={draft.status === "saving" ? {} : { scale: 0.995 }}
                                  className={`flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-[12.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${draft.status === "error"
                                    ? "border border-red-500/20 bg-[linear-gradient(180deg,#dc2626_0%,#b91c1c_100%)] text-white shadow-[0_8px_22px_-10px_rgba(220,38,38,0.5)]"
                                    : draft.status === "dirty"
                                      ? "border border-fk-accent bg-fk-accent text-white shadow-[0_6px_18px_-6px_rgba(37,99,235,0.35)] hover:brightness-110"
                                      : "border border-slate-200 bg-white text-slate-500 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-400"
                                    }`}
                                  title={
                                    draft.status === "error"
                                      ? `The last save failed: ${draft.error}`
                                      : saveIsIdle
                                        ? "Everything on this table is saved. Click to write it again anyway."
                                        : "Write your screening choices, margin, customs and extras to this quote"
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={saveLook.icon}
                                    className={`text-[12px] ${saveLook.spin ? "animate-spin" : ""}`}
                                  />
                                  {saveLook.label}
                                </motion.button>
                                {draft.status === "error" && (
                                  <p className="-mt-1 text-[10.5px] leading-snug text-red-600 dark:text-red-400">
                                    {draft.error}
                                  </p>
                                )}
                                {typeof triggerPanel === "function" && (
                                  <>
                                    <motion.button
                                      onClick={sendToCustomer}
                                      disabled={!canSend}
                                      whileHover={canSend ? { y: -1 } : {}}
                                      whileTap={canSend ? { scale: 0.995 } : {}}
                                      className={`flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-[12.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${canSend
                                        ? "border border-fk-accent bg-fk-accent text-white shadow-[0_6px_18px_-6px_rgba(37,99,235,0.35)] hover:brightness-110"
                                        : "border border-slate-200 bg-slate-50 text-slate-400 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-500"
                                        }`}
                                      title={
                                        picked === 0
                                          ? "Tick one or more rows to send them to the customer"
                                          : canSend
                                            ? "Attach these options to the Customer Conversation so the customer can choose"
                                            : "Every ticked row needs a screening type and a total above £0 before it can be sent"
                                      }
                                    >
                                      <FontAwesomeIcon icon={faPaperPlane} className="text-[12px]" />
                                      {sendLabel}
                                    </motion.button>
                                    {picked > 0 && !canSend && (
                                      <p className="-mt-1 text-[10.5px] leading-snug text-amber-600 dark:text-amber-400">
                                        {picked - priced.length} of the {picked} ticked rows still needs a screening type.
                                      </p>
                                    )}
                                    <div className="my-0.5 flex items-center gap-2">
                                      <span className="h-px flex-1 bg-slate-200 dark:bg-[oklch(0.33_0.022_264)]" />
                                      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                                        then, once they choose
                                      </span>
                                      <span className="h-px flex-1 bg-slate-200 dark:bg-[oklch(0.33_0.022_264)]" />
                                    </div>
                                  </>
                                )}
                                <ol className="flex flex-col gap-1.5" aria-label="Steps remaining before you can finalise">
                                  {steps.map((step) => (
                                    <li key={step.label} className="flex items-start gap-2">
                                      <FontAwesomeIcon
                                        icon={step.done ? faCircleCheck : faCircleInfo}
                                        className={`mt-[2px] text-[11px] ${step.done ? "text-emerald-600 dark:text-emerald-400" : "text-slate-300 dark:text-slate-600"}`}
                                        aria-hidden
                                      />
                                      <div className="min-w-0">
                                        <p className={`text-[11.5px] leading-snug ${step.done ? "font-medium text-slate-400 line-through dark:text-slate-600" : "font-semibold text-slate-700 dark:text-slate-200"}`}>
                                          {step.label}
                                        </p>
                                        {!step.done && (
                                          <p className="text-[10.5px] leading-snug text-slate-500 dark:text-slate-400">
                                            {step.hint}
                                          </p>
                                        )}
                                      </div>
                                    </li>
                                  ))}
                                </ol>

                                <motion.button
                                  onClick={finalizeANomination}
                                  disabled={!ready}
                                  whileHover={ready ? { y: -1 } : {}}
                                  whileTap={ready ? { scale: 0.995 } : {}}
                                  className={`mt-0.5 flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-[12.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${ready
                                    ? "border border-emerald-600/10 bg-[linear-gradient(180deg,#059669_0%,#047857_100%)] text-white shadow-[0_8px_22px_-10px_rgba(5,150,105,0.5)]"
                                    : "border border-slate-200 bg-slate-50 text-slate-400 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-500"
                                    }`}
                                  title={ready ? "Nominate this option as the winning quote" : `Still to do: ${blockers[0].label}`}
                                >
                                  <FontAwesomeIcon icon={faCircleCheck} className="text-[12px]" />
                                  Finalise A Quote
                                </motion.button>

                                {/* Freezing is optional now. It used to be the ONLY
                                    way to reveal the finalise button, while also
                                    disabling the screening tiles — so locking before
                                    picking a screening was an inescapable dead end.
                                    It is called Lock, not Freeze-with-a-floppy-disk:
                                    it persists nothing, and two controls that look
                                    like Save is one too many. */}
                                <motion.button
                                  onClick={toggleSaveThisTable}
                                  whileHover={{ y: -1 }}
                                  whileTap={{ scale: 0.995 }}
                                  title={saveThisTable ? "Reopen the table to adjust screening, margin or extras" : "Optional: make the figures read-only so they can't be changed by accident. This does not save anything."}
                                  className={`flex w-full items-center justify-center gap-2 rounded-xl border px-3 py-2 text-[12.5px] font-semibold transition-colors ${saveThisTable
                                    ? "border-amber-300/80 bg-amber-500 text-white shadow-md hover:bg-amber-600 dark:border-amber-400/50 dark:bg-amber-600 dark:hover:bg-amber-500"
                                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-300"
                                    }`}
                                >
                                  <FontAwesomeIcon icon={saveThisTable ? faLockOpen : faLock} className="text-[12px]" />
                                  {saveThisTable ? "Unlock & Edit" : "Lock figures (optional)"}
                                </motion.button>
                              </div>
                            );
                          })()}
                        </aside>
                      </div>
                    ) : (
                      // Export row in customer preview. "Back to Internal View"
                      // was removed — the header pill toggle is now the single
                      // source of truth for view switching. Re-skinned with
                      // motion.button to match the rest of the modal (the
                      // legacy CustomButton2 with yellow/blue/teal colors
                      // clashed visually).
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {typeof triggerPanel === "function" && (
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              const selectedOptions = allOtherQuoteOptions.flat().filter((option) =>
                                option.screeningPrices.map((x) => x.selectBox).includes(true)
                              );
                              const codes = selectedOptions.map((X) =>
                                (X?.agentCode || "") + "_" + (X?.screeningPrices?.map((x) => x?.destinationAirport).join("-") || "")
                              );
                              rememberSentOptions(selectedOptions);
                              triggerMyPanel(e, codes, undefined, undefined, undefined, true);
                            }}
                            whileHover={{ y: -1 }}
                            whileTap={{ scale: 0.96 }}
                            className="inline-flex items-center gap-2 rounded-xl border border-emerald-600/10 bg-[linear-gradient(180deg,#059669_0%,#047857_100%)] px-4 py-2.5 text-[12.5px] font-semibold text-white shadow-[0_8px_22px_-10px_rgba(5,150,105,0.5)] hover:brightness-110 active:brightness-95 dark:border-emerald-400/30"
                            title="Send the selected customer-view table(s) to the Customer Conversation reply composer"
                          >
                            <FontAwesomeIcon icon={faPaperPlane} className="text-[12px]" />
                            Send to Customer Conversation
                          </motion.button>
                        )}
                        <motion.button
                          type="button"
                          onClick={handlePrint}
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.96 }}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200 dark:hover:bg-[oklch(0.26_0.028_264)]"
                        >
                          <FontAwesomeIcon icon={faPrint} className="text-[11px]" />
                          Print
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={handleDownloadCsv}
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.96 }}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200 dark:hover:bg-[oklch(0.26_0.028_264)]"
                        >
                          <FontAwesomeIcon icon={faFileCsv} className="text-[11px]" />
                          Download CSV
                        </motion.button>
                        {/* "Just send a screenshot to the customer" — the
                            operator's own words. Until now that meant an OS
                            screenshot; FreightKit had no image export at all. */}
                        <motion.button
                          type="button"
                          onClick={handleCopyImage}
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.96 }}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200 dark:hover:bg-[oklch(0.26_0.028_264)]"
                        >
                          <FontAwesomeIcon icon={faCopy} className="text-[11px]" />
                          Copy image
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={handleDownloadPdf}
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.96 }}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200 dark:hover:bg-[oklch(0.26_0.028_264)]"
                        >
                          <FontAwesomeIcon icon={faFilePdf} className="text-[11px]" />
                          Download PDF
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={handleCopyForGmail}
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.96 }}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200 dark:hover:bg-[oklch(0.26_0.028_264)]"
                        >
                          <FontAwesomeIcon icon={faEnvelope} className="text-[11px]" />
                          Copy for Gmail
                        </motion.button>
                        {quoteSource === "manual" ? (
                          <motion.button
                            type="button"
                            onClick={handleCopyCustomerView}
                            whileHover={{ y: -1 }}
                            whileTap={{ scale: 0.96 }}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200 dark:hover:bg-[oklch(0.26_0.028_264)]"
                          >
                            <FontAwesomeIcon icon={faCopy} className="text-[11px]" />
                            Copy customer view
                          </motion.button>
                        ) : null}
                      </div>
                    ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
        , document.body
      )}
      {unsavedPrompt && createPortal(
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="fk-unsaved-title"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-[420px] rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)]"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                <FontAwesomeIcon icon={faTriangleExclamation} className="text-[13px]" />
              </span>
              <div className="min-w-0">
                <h4
                  id="fk-unsaved-title"
                  className="text-[14px] font-semibold text-slate-900 dark:text-slate-100"
                >
                  You have unsaved pricing
                </h4>
                <p className="mt-1 text-[12px] leading-snug text-slate-600 dark:text-slate-400">
                  {draft.status === "error"
                    ? `The last save failed: ${draft.error}. Close now and this pricing is lost.`
                    : "Your screening choices, margin, customs and extras are not in this quote yet."}
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-col gap-2">
              <motion.button
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.995 }}
                disabled={draft.status === "saving"}
                onClick={async () => {
                  const outcome = await savePricing();
                  // Only leave if the work actually landed. Closing on a failed
                  // save would discard exactly what the dialog promised to keep.
                  if (outcome.ok) {
                    setUnsavedPrompt(false);
                    setShowModal(false);
                  }
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-fk-accent bg-fk-accent px-3 py-2 text-[12.5px] font-semibold text-white shadow-[0_6px_18px_-6px_rgba(37,99,235,0.35)] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FontAwesomeIcon
                  icon={draft.status === "saving" ? faCircleNotch : faSave}
                  className={`text-[12px] ${draft.status === "saving" ? "animate-spin" : ""}`}
                />
                {draft.status === "saving" ? "Saving…" : "Save & close"}
              </motion.button>
              <div className="flex gap-2">
                <button
                  onClick={() => setUnsavedPrompt(false)}
                  className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-600 hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-300"
                >
                  Keep editing
                </button>
                <button
                  onClick={() => {
                    setUnsavedPrompt(false);
                    setShowModal(false);
                  }}
                  className="flex-1 rounded-xl border border-transparent px-3 py-2 text-[12.5px] font-semibold text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                >
                  Discard changes
                </button>
              </div>
            </div>
          </motion.div>
        </div>
        , document.body
      )}
    </MaybeAccordion>
  );
}

export default memo(FinalQuoteCostTable);
